import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import Logo from './Logo';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import { hasAccess, ALLOWED_ROLES, getUserRole, getTenantIdTitleCase } from '../utils/roleUtils';
import * as XLSX from 'xlsx';

// Función para obtener fechas dinámicas (últimos 7 días) usando zona horaria local
const getDefaultDateRange = () => {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 6); // 7 días atrás (incluyendo hoy)

  const formatDate = (date) => {
    // Usar zona horaria local en lugar de UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const dateRange = {
    startDate: formatDate(startDate),
    endDate: formatDate(today)
  };

  // Log para verificar fechas calculadas (zona horaria local)
  // console.log('📅 Fechas dinámicas calculadas (zona horaria local):', dateRange);

  return dateRange;
};

function DashboardEjecutivo() {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [preferredName, setPreferredName] = useState('');
  const [error, setError] = useState(null);
  // Rol del usuario y control de aplicación inicial de restricción
  const [userRole, setUserRole] = useState(null);
  const [initialRoleFilterApplied, setInitialRoleFilterApplied] = useState(false);

  // Estados para filtros
  const [filters, setFilters] = useState(() => {
    const dateRange = getDefaultDateRange();
    return {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      retailer: '',
      brand: '',
      store: ''
    };
  });

  // Estado para colapsar filtros (por defecto colapsado)
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  // Modo de gráfica principal
  const [chartMode, setChartMode] = useState('transacciones'); // 'transacciones' | 'cotizaciones'
  // Estado para controlar visibilidad de datos personales
  const [showPersonalData, setShowPersonalData] = useState(false);

  // Estados para datos (inicialmente vacío, se llenan con datos reales)
  const [dashboardData, setDashboardData] = useState({
    kpis: {
      totalCotizaciones: 0,
      totalRetomas: 0,
      totalVentasGeneradas: 0,
      totalMontoRefurbi: 0,
      totalMontoMarcas: 0,
      tiendasActivas: 0
    },
    dailyTrends: [],
    stackedData: {},
    retailers: [],
    brands: [],
    stores: [],
    topStores: [],
    topPromoters: [],
    topDevicesRetomas: [],
    topDevicesVendidos: [],
    topCities: [],
    topBrandRetomas: [],
    gradeDistribution: {},
    typeDistribution: {},
    dailyComboMoney: {},
    dailyComboTransacciones: {}
  });

  // Estados para datos reales
  const [realData, setRealData] = useState({
    quotations: [],
    transactions: [],
    processedData: null,
    lastUpdated: null
  });

  // Listas completas para opciones de selects (no afectadas por filtros aplicados)
  const [allRetailers, setAllRetailers] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [allStores, setAllStores] = useState([]);

  // Helpers para primera marca/retailer disponible en datos
  const getFirstBrandFromData = (processed, quotations, transactions) => {
    const list = (processed?.brands && processed.brands.length > 0)
      ? processed.brands
      : Array.from(new Set([
        ...(quotations || []).map(q => q.marca).filter(Boolean),
        ...(transactions || []).map(t => t.brand).filter(Boolean)
      ])).sort();
    return list[0] || '';
  };

  const getFirstRetailerFromData = (processed, quotations, transactions) => {
    const list = (processed?.retailers && processed.retailers.length > 0)
      ? processed.retailers
      : Array.from(new Set([
        ...(quotations || []).map(q => q.retailer).filter(Boolean),
        ...(transactions || []).map(t => t.retailer).filter(Boolean)
      ])).sort();
    return list[0] || '';
  };

  // Enforzar reglas de visibilidad por rol al cargar/aplicar
  const enforceRoleRestrictions = async (rawTransactions, rawQuotations, processed) => {
    try {
      const role = userRole || await getUserRole();
      if (!role) return;

      // Si ya se aplicó en este ciclo de carga, no repetir
      if (initialRoleFilterApplied) return;

      if (role === 'admin_retailer') {
        const firstBrand = getFirstBrandFromData(processed, rawQuotations, rawTransactions);
        if (firstBrand) {
          const enforcedFilters = { ...filters, brand: firstBrand };
          setFilters(enforcedFilters);
          // Aplicar a datos ya procesados manteniendo consistencia de estructura
          const filteredData = applyFiltersToProcessedData(
            rawTransactions || [],
            rawQuotations || [],
            enforcedFilters
          );
          if (filteredData) {
            setDashboardData({ ...filteredData, rawTransactionData: rawTransactions || [] });
          }
          setInitialRoleFilterApplied(true);
        }
      } else if (role === 'admin_marca') {
        const firstRetailer = getFirstRetailerFromData(processed, rawQuotations, rawTransactions);
        if (firstRetailer) {
          const enforcedFilters = { ...filters, retailer: firstRetailer };
          setFilters(enforcedFilters);
          const filteredData = applyFiltersToProcessedData(
            rawTransactions || [],
            rawQuotations || [],
            enforcedFilters
          );
          if (filteredData) {
            setDashboardData({ ...filteredData, rawTransactionData: rawTransactions || [] });
          }
          setInitialRoleFilterApplied(true);
        }
      }
    } catch (_) {
      // Silencioso: ante error no romper la UI
    }
  };

  // Obtener nombre preferido de Cognito
  useEffect(() => {
    const getPreferredName = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        if (idToken) {
          const payload = idToken.payload || {};
          const name = payload.preferred_username ||
            payload.given_name ||
            payload.name ||
            payload.email?.split('@')[0] ||
            'Usuario';
          setPreferredName(name);
        }
      } catch (error) {
        console.error('Error al obtener nombre preferido:', error);
      }
    };

    getPreferredName();
  }, []);

  // Función helper para obtener textos dinámicos basados en tenant_id
  const getDynamicTenantTexts = () => {
    const tenantId = localStorage.getItem('tenant_id');
    if (tenantId) {
      const titleCaseTenant = getTenantIdTitleCase(tenantId);
      return {
        totalAmountLabel: `Monto Total Entregado por ${titleCaseTenant}`,
        doubleBarsLabel: `Barras dobles: ${titleCaseTenant} (izq) y Marcas (der) segmentadas por retailer-marca`
      };
    }
    return {
      totalAmountLabel: 'Monto Total Entregado por Refurbi', // Fallback
      doubleBarsLabel: 'Barras dobles: Refurbi (izq) y Marcas (der) segmentadas por retailer-marca' // Fallback
    };
  };

  // Función para obtener headers con autenticación Cognito
  const getAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No se pudo obtener el ID token');
      }

      return {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error al obtener el token de autenticación:', error);
      throw error;
    }
  };

  // Función para parsear un registro de la API
  const parseEventRecord = (record) => {
    const { count, fecha_marca_retailer_tienda, fecha_retailer_marca_tienda, fecha_tienda_retailer_marca, tenant_id_event_name } = record;

    // Aceptar múltiples combinaciones nuevas desde backend
    const composite = fecha_marca_retailer_tienda || fecha_retailer_marca_tienda || fecha_tienda_retailer_marca;
    if (!composite || typeof composite !== 'string') {
      return null;
    }

    const parts = composite.split('#');
    if (parts.length !== 4) {
      return null;
    }

    let fecha = parts[0];
    let marca = '';
    let retailer = '';
    let tienda = '';

    if (fecha_marca_retailer_tienda) {
      // [fecha, marca, retailer, tienda]
      [, marca, retailer, tienda] = parts;
    } else if (fecha_retailer_marca_tienda) {
      // [fecha, retailer, marca, tienda]
      [, retailer, marca, tienda] = parts;
    } else if (fecha_tienda_retailer_marca) {
      // [fecha, tienda, retailer, marca]
      [, tienda, retailer, marca] = parts;
    }

    // Parsear tenant_id_event_name: "REFURBI#QuotationCreated"
    const eventParts = (tenant_id_event_name || '').split('#');
    const eventName = eventParts.length > 1 ? eventParts[1] : eventParts[0];

    return {
      fecha,
      marca: marca || 'Sin Marca',
      retailer: retailer || 'Sin Retailer',
      tienda: tienda || 'Sin Tienda',
      count: parseFloat(count) || 0,
      eventName
    };
  };

  // Función para obtener datos de eventos desde la API
  const fetchEventData = async (eventName, startDate, endDate) => {
    try {
      const tenantId = localStorage.getItem('tenant_id');
      const headers = await getAuthHeaders();

      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/events',
        {
          headers,
          params: {
            tenant_id: tenantId,
            event: eventName,
            start: startDate,
            end: endDate
          }
        }
      );

      // LOG para ver la estructura real
      // console.log(`RAW DATA [${eventName}]:`, response.data);

      // Usar directamente el array recibido
      return Array.isArray(response.data)
        ? response.data.map(parseEventRecord).filter(record => record !== null)
        : [];
    } catch (error) {
      // console.error(`Error al obtener datos de ${eventName}:`, error);
      return [];
    }
  };

  // Función para convertir fecha YYYY-MM-DD a epoch en segundos
  const convertDateToEpoch = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00.000Z'); // Usar UTC para evitar problemas de zona horaria
    return Math.floor(date.getTime() / 1000);
  };

  // Función para obtener datos de transacciones finalizadas
  const fetchTransactionData = async (startDate, endDate) => {
    try {
      const tenantId = localStorage.getItem('tenant_id');
      const headers = await getAuthHeaders();

      // Convertir fechas a formato epoch en segundos
      const startDateEpoch = convertDateToEpoch(startDate);
      const endDateEpoch = convertDateToEpoch(endDate);

      // console.log('🔄 Solicitando datos de transacciones...');
      // console.log('📅 Fechas:', { startDate, endDate });
      // console.log('⏰ Fechas en epoch:', { startDateEpoch, endDateEpoch });

      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/transactions',
        {
          headers,
          params: {
            tenant_id: tenantId,
            mode: 'overview',
            start_date: startDateEpoch,
            end_date: endDateEpoch,
            returning_type: 'min' // Modo ligero para visualización
          }
        }
      );

      // console.log('📊 RAW TRANSACTION DATA:', response.data);
      // console.log('📊 Tipo de datos:', typeof response.data);
      // console.log('📊 Es array:', Array.isArray(response.data));
      // console.log('📊 Longitud:', response.data?.length);

      // Si es un array, mostrar las primeras 3 entradas para ver la estructura
      if (Array.isArray(response.data) && response.data.length > 0) {
        // console.log('📊 Primeras 3 transacciones:', response.data.slice(0, 3));
        // console.log('📊 Estructura de la primera transacción:', Object.keys(response.data[0]));
      }

      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      // console.error('❌ Error al obtener datos de transacciones:', error);
      // console.error('❌ Detalles del error:', error.response?.data);
      return [];
    }
  };

  // Función para obtener CSV completo desde S3 para exportación
  const fetchTransactionDataForExport = async (startDate, endDate) => {
    try {
      const tenantId = localStorage.getItem('tenant_id');
      const headers = await getAuthHeaders();

      // Convertir fechas a formato epoch en segundos
      const startDateEpoch = convertDateToEpoch(startDate);
      const endDateEpoch = convertDateToEpoch(endDate);

      console.log('📦 Solicitando datos completos para exportación...');
      console.log('📅 Fechas:', { startDate, endDate });

      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/transactions',
        {
          headers,
          params: {
            tenant_id: tenantId,
            mode: 'overview',
            start_date: startDateEpoch,
            end_date: endDateEpoch,
            returning_type: 'complete' // Modo completo: devuelve URL de S3
          }
        }
      );

      console.log('📦 Respuesta del servidor:', response.data);

      // Validar estructura de respuesta
      if (!response.data || !response.data.download_url) {
        throw new Error('La respuesta no contiene un URL de descarga válido');
      }

      const { download_url, total_items, expires_in } = response.data;
      console.log(`📦 CSV disponible para descarga (${total_items} registros, expira en ${expires_in}s)`);

      // Descargar el CSV desde S3 (ahora CORS está configurado)
      console.log('⬇️ Descargando CSV desde S3...');
      const csvResponse = await axios.get(download_url, {
        responseType: 'text'
      });

      console.log('✅ CSV descargado exitosamente');

      // Parsear CSV a array de objetos
      const parsedData = parseCSV(csvResponse.data);
      console.log(`✅ CSV parseado: ${parsedData.length} registros`);

      return parsedData;
    } catch (error) {
      console.error('❌ Error al obtener datos completos para exportación:', error);
      console.error('❌ Detalles del error:', error.response?.data);
      throw error; // Propagamos el error para manejarlo en handleExportExcel
    }
  };

  // Función helper para parsear CSV a array de objetos
  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length === 0) return [];

    // Primera línea contiene los headers
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));

    // Resto de líneas contienen los datos
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      // Parsear CSV respetando comillas
      const values = [];
      let currentValue = '';
      let insideQuotes = false;

      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        const nextChar = line[j + 1];

        if (char === '"' && !insideQuotes) {
          insideQuotes = true;
        } else if (char === '"' && insideQuotes && nextChar === '"') {
          currentValue += '"';
          j++; // Skip next quote
        } else if (char === '"' && insideQuotes) {
          insideQuotes = false;
        } else if (char === ',' && !insideQuotes) {
          values.push(currentValue.trim());
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue.trim()); // Último valor

      // Crear objeto usando headers
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = values[index] || '';
      });
      data.push(obj);
    }

    return data;
  };

  // Función para procesar datos reales de transacciones
  const processRealTransactionData = (realTransactionData) => {
    if (!realTransactionData || realTransactionData.length === 0) {
      // console.log('📊 No hay datos reales de transacciones, usando datos mock');
      return null;
    }

    // console.log('🔄 Procesando datos reales de transacciones...');

    // Procesar KPIs (solo finalizadas aportan a KPIs de negocio)
    const onlyFinalizadas = realTransactionData.filter(t => (t.tradein_status || '').toLowerCase() === 'finalizada');
    const totalCanceladas = realTransactionData.filter(t => (t.tradein_status || '').toLowerCase() === 'cancelada').length;
    const totalRetomas = onlyFinalizadas.length;
    const totalVentasGeneradas = onlyFinalizadas.reduce((sum, t) => sum + parseFloat(t.sold_device_price || 0), 0);
    const totalMontoRefurbi = onlyFinalizadas.reduce((sum, t) => sum + parseFloat(t.bonus_amount_trader || 0), 0);
    const totalMontoMarcas = onlyFinalizadas.reduce((sum, t) => sum + parseFloat(t.bonus_amount_campaign || 0), 0);

    // Obtener listas únicas
    const retailers = [...new Set(realTransactionData.map(t => t.retailer))].filter(Boolean).sort();
    const brands = [...new Set(realTransactionData.map(t => t.brand))].filter(Boolean).sort();
    const stores = [...new Set(realTransactionData.map(t => t.store_name))].filter(Boolean).sort();

    // Top 5 tiendas por retomas
    const storeRetomas = onlyFinalizadas.reduce((acc, t) => {
      const key = t.store_name;
      if (!acc[key]) acc[key] = { name: key, retailer: t.retailer, retomas: 0 };
      acc[key].retomas++;
      return acc;
    }, {});
    const topStores = Object.values(storeRetomas).sort((a, b) => b.retomas - a.retomas).slice(0, 5);

    // Top 5 promotores
    const promoterStats = onlyFinalizadas.reduce((acc, t) => {
      const key = t.promoter_id;
      if (!acc[key]) acc[key] = {
        id: key,
        name: t.promoter_name,
        quantity: 0,
        totalRefurbi: 0,
        totalMarcas: 0
      };
      acc[key].quantity++;
      acc[key].totalRefurbi += parseFloat(t.bonus_amount_trader || 0);
      acc[key].totalMarcas += parseFloat(t.bonus_amount_campaign || 0);
      return acc;
    }, {});
    const topPromoters = Object.values(promoterStats).sort((a, b) => b.quantity - a.quantity).slice(0, 5);

    // Top 5 dispositivos más retomados
    const deviceRetomas = onlyFinalizadas.reduce((acc, t) => {
      const key = t.buyback_device_name_std;
      if (!acc[key]) acc[key] = {
        brand: t.buyback_device_brand,
        model: key,
        quantity: 0
      };
      acc[key].quantity++;
      return acc;
    }, {});
    const topDevicesRetomas = Object.values(deviceRetomas).sort((a, b) => b.quantity - a.quantity).slice(0, 5);

    // Top 5 dispositivos más vendidos (con valor total)
    const deviceVentas = onlyFinalizadas.reduce((acc, t) => {
      const key = t.sold_device_name;
      if (!acc[key]) acc[key] = {
        brand: t.brand,
        model: key,
        quantity: 0,
        totalValue: 0
      };
      acc[key].quantity++;
      acc[key].totalValue += parseFloat(t.sold_device_price || 0);
      return acc;
    }, {});
    const topDevicesVendidos = Object.values(deviceVentas).sort((a, b) => b.totalValue - a.totalValue).slice(0, 5);

    // Top 5 ciudades
    const cityStats = onlyFinalizadas.reduce((acc, t) => {
      const key = t.store_city;
      if (!acc[key]) acc[key] = { name: key, quantity: 0 };
      acc[key].quantity++;
      return acc;
    }, {});
    const topCities = Object.values(cityStats).sort((a, b) => b.quantity - a.quantity).slice(0, 5);

    // Top 5 marcas de dispositivos retomados
    const brandRetomas = onlyFinalizadas.reduce((acc, t) => {
      const key = t.buyback_device_brand;
      if (!acc[key]) acc[key] = { brand: key, quantity: 0 };
      acc[key].quantity++;
      return acc;
    }, {});
    const topBrandRetomas = Object.values(brandRetomas).sort((a, b) => b.quantity - a.quantity).slice(0, 5);

    // Distribución de grados
    const gradeDistribution = onlyFinalizadas.reduce((acc, t) => {
      const grade = t.buyback_device_final_grade;
      if (!acc[grade]) acc[grade] = 0;
      acc[grade]++;
      return acc;
    }, {});

    // Distribución de tipos de transacción
    const typeDistribution = realTransactionData.reduce((acc, t) => {
      const type = t.tradein_type;
      if (!acc[type]) acc[type] = 0;
      acc[type]++;
      return acc;
    }, {});

    // Crear datos diarios reales basados en timestamps reales
    const dailyData = realTransactionData.reduce((acc, t) => {
      const date = new Date(t.timestamp * 1000).toISOString().split('T')[0];
      if (!acc[date]) acc[date] = {
        date,
        retomas: 0, // finalizadas
        canceladas: 0,
        montoRefurbi: 0,
        montoMarcas: 0,
        ventasGeneradas: 0
      };
      if ((t.tradein_status || '').toLowerCase() === 'finalizada') acc[date].retomas++;
      if ((t.tradein_status || '').toLowerCase() === 'cancelada') acc[date].canceladas++;
      acc[date].montoRefurbi += parseFloat(t.bonus_amount_trader || 0);
      acc[date].montoMarcas += parseFloat(t.bonus_amount_campaign || 0);
      acc[date].ventasGeneradas += parseFloat(t.sold_device_price || 0);
      return acc;
    }, {});

    const dailyTrends = Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date));

    // Datos apilados por retailer-marca (solo datos reales)
    const stackedData = onlyFinalizadas.reduce((acc, t) => {
      const key = `${t.retailer}-${t.brand}`;
      if (!acc[key]) acc[key] = {
        retailer: t.retailer,
        marca: t.brand,
        retomas: 0,
        cotizaciones: 0,
        montoRefurbi: 0,
        montoMarcas: 0
      };
      acc[key].retomas++;
      acc[key].montoRefurbi += parseFloat(t.bonus_amount_trader || 0);
      acc[key].montoMarcas += parseFloat(t.bonus_amount_campaign || 0);
      return acc;
    }, {});

    // Procesar datos reales por día y combo para montos
    const dailyComboMoney = onlyFinalizadas.reduce((acc, t) => {
      const date = new Date(t.timestamp * 1000).toISOString().slice(0, 10);
      const key = `${t.retailer}-${t.brand}`;
      if (!acc[date]) acc[date] = {};
      if (!acc[date][key]) acc[date][key] = { montoRefurbi: 0, montoMarcas: 0 };
      acc[date][key].montoRefurbi += parseFloat(t.bonus_amount_trader || 0);
      acc[date][key].montoMarcas += parseFloat(t.bonus_amount_campaign || 0);
      return acc;
    }, {});

    // Procesar datos reales por día y combo para transacciones
    const dailyComboTransacciones = realTransactionData.reduce((acc, t) => {
      const date = new Date(t.timestamp * 1000).toISOString().slice(0, 10);
      const key = `${t.retailer}-${t.brand}`;
      if (!acc[date]) acc[date] = {};
      if (!acc[date][key]) acc[date][key] = { retomas: 0, canceladas: 0, cotizaciones: 0 };
      if ((t.tradein_status || '').toLowerCase() === 'finalizada') acc[date][key].retomas++;
      if ((t.tradein_status || '').toLowerCase() === 'cancelada') acc[date][key].canceladas++;
      return acc;
    }, {});

    // console.log('✅ Datos reales procesados exitosamente');

    return {
      kpis: {
        totalRetomas,
        totalCanceladas,
        totalVentasGeneradas,
        totalMontoRefurbi,
        totalMontoMarcas,
        tiendasActivas: stores.length
      },
      dailyTrends,
      stackedData,
      retailers,
      brands,
      stores,
      topStores,
      topPromoters,
      topDevicesRetomas,
      topDevicesVendidos,
      topCities,
      topBrandRetomas,
      gradeDistribution,
      typeDistribution,
      dailyComboMoney,
      dailyComboTransacciones,
      rawTransactionData: realTransactionData
    };
  };

  // Función para procesar y agregar datos
  const processEventData = (quotationData, transactionData) => {
    // Crear mapas para agregación rápida
    const quotationMap = new Map();
    const transactionMap = new Map();
    const dailyQuotations = new Map();
    const dailyTransactions = new Map();

    // Procesar cotizaciones
    quotationData.forEach(record => {
      const { fecha, marca, retailer, tienda, count } = record;

      // Agregar por fecha
      dailyQuotations.set(fecha, (dailyQuotations.get(fecha) || 0) + count);

      // Agregar por retailer-marca
      const key = `${retailer}-${marca}`;
      if (!quotationMap.has(key)) {
        quotationMap.set(key, { retailer, marca, cotizaciones: 0, retomas: 0 });
      }
      quotationMap.get(key).cotizaciones += count;
    });

    // Procesar transacciones
    transactionData.forEach(record => {
      const { fecha, marca, retailer, tienda, count } = record;

      // Agregar por fecha
      dailyTransactions.set(fecha, (dailyTransactions.get(fecha) || 0) + count);

      // Agregar por retailer-marca
      const key = `${retailer}-${marca}`;
      if (!transactionMap.has(key)) {
        transactionMap.set(key, { retailer, marca, cotizaciones: 0, retomas: 0 });
      }
      transactionMap.get(key).retomas += count;
    });

    // Combinar datos de cotizaciones y transacciones
    const combinedMap = new Map();

    // Agregar cotizaciones
    quotationMap.forEach((value, key) => {
      combinedMap.set(key, { ...value });
    });

    // Agregar transacciones
    transactionMap.forEach((value, key) => {
      if (combinedMap.has(key)) {
        combinedMap.get(key).retomas = value.retomas;
      } else {
        combinedMap.set(key, { ...value });
      }
    });

    // Calcular KPIs
    const totalCotizaciones = Array.from(quotationMap.values()).reduce((sum, item) => sum + item.cotizaciones, 0);
    const totalRetomas = Array.from(transactionMap.values()).reduce((sum, item) => sum + item.retomas, 0);

    // Crear datos diarios para gráficas
    const allDates = new Set([...dailyQuotations.keys(), ...dailyTransactions.keys()]);
    const dailyTrends = Array.from(allDates).sort().map(date => ({
      date,
      cotizaciones: dailyQuotations.get(date) || 0,
      retomas: dailyTransactions.get(date) || 0,
      // Mantener los montos mock por ahora
      montoRefurbi: 400000000,
      montoMarcas: 300000000
    }));

    // Crear datos apilados para gráficas
    const stackedData = {};
    combinedMap.forEach((value, key) => {
      stackedData[key] = {
        retomas: value.retomas,
        cotizaciones: value.cotizaciones,
        monto: 0 // No disponible en función legacy
      };
    });

    // Obtener listas únicas para filtros
    const retailers = [...new Set(Array.from(combinedMap.values()).map(item => item.retailer))].sort();
    const brands = [...new Set(Array.from(combinedMap.values()).map(item => item.marca))].sort();
    const stores = [...new Set([...quotationData, ...transactionData].map(item => item.tienda))].sort();

    // Generar estructura dailyComboTransacciones (solo cotizaciones y transacciones agrupadas legacy)
    const dailyComboTransacciones = {};
    quotationData.forEach(q => {
      const date = q.fecha;
      const key = `${q.retailer}-${q.marca}`;
      if (!dailyComboTransacciones[date]) dailyComboTransacciones[date] = {};
      if (!dailyComboTransacciones[date][key]) dailyComboTransacciones[date][key] = { retomas: 0, cotizaciones: 0 };
      dailyComboTransacciones[date][key].cotizaciones += q.count || 0;
    });
    transactionData.forEach(t => {
      const date = t.fecha;
      const key = `${t.retailer}-${t.marca}`;
      if (!dailyComboTransacciones[date]) dailyComboTransacciones[date] = {};
      if (!dailyComboTransacciones[date][key]) dailyComboTransacciones[date][key] = { retomas: 0, cotizaciones: 0 };
      dailyComboTransacciones[date][key].retomas += t.count || 0;
    });

    return {
      kpis: {
        totalCotizaciones,
        totalRetomas,
        tiendasActivas: stores.length,
        montoTotalRefurbi: 0, // No disponible en función legacy
        montoTotalMarcas: 0   // No disponible en función legacy
      },
      dailyTrends,
      stackedData,
      dailyComboTransacciones,
      retailers,
      brands,
      stores,
      // Rankings vacíos (esta función ya no se usa, se usan datos reales)
      topStores: [],
      topDevicesRetomas: [],
      topDevicesCotizaciones: [],
      topDevicesCompras: []
    };
  };

  // Función para aplicar filtros a los datos cargados
  const applyFiltersToData = (quotationData, transactionData, currentFilters) => {
    const filterData = (data, filters) => {
      return data.filter(record => {
        if (filters.retailer && record.retailer !== filters.retailer) return false;
        if (filters.brand && record.marca !== filters.brand) return false;
        if (filters.store && record.tienda !== filters.store) return false;
        return true;
      });
    };

    const filteredQuotations = filterData(quotationData, currentFilters);
    const filteredTransactions = filterData(transactionData, currentFilters);

    return processEventData(filteredQuotations, filteredTransactions);
  };

  // Función para cargar datos reales
  const loadRealData = async (autoCollapse = false) => {
    setLoading(true);
    setError(null);
    try {
      // Siempre re-aplicar regla por rol después de recarga
      setInitialRoleFilterApplied(false);
      // console.log('🔄 Cargando datos reales...');

      // Obtener datos de cotizaciones y transacciones en paralelo
      const [quotationData, transactionData] = await Promise.all([
        fetchEventData('QuotationCreated', filters.startDate, filters.endDate),
        fetchTransactionData(filters.startDate, filters.endDate)
      ]);

      // Calcular listas completas de opciones para selects
      const retailersAll = Array.from(new Set([
        ...transactionData.map(t => t.retailer).filter(Boolean),
        ...quotationData.map(q => q.retailer).filter(Boolean)
      ])).sort();
      const brandsAll = Array.from(new Set([
        ...transactionData.map(t => t.brand).filter(Boolean),
        ...quotationData.map(q => q.marca).filter(Boolean)
      ])).sort();
      const storesAll = Array.from(new Set([
        ...transactionData.map(t => t.store_name).filter(Boolean),
        ...quotationData.map(q => q.tienda).filter(Boolean)
      ])).sort();
      setAllRetailers(retailersAll);
      setAllBrands(brandsAll);
      setAllStores(storesAll);

      // console.log('📊 Cotizaciones obtenidas:', quotationData.length);
      // console.log('✅ Transacciones obtenidas:', transactionData.length);
      // console.log('📈 Datos de transacciones para procesamiento:', transactionData.length > 0 ? 'Datos reales disponibles' : 'Sin datos - usando mock');

      // Guardar datos raw
      setRealData({
        quotations: quotationData,
        transactions: transactionData,
        processedData: null,
        lastUpdated: new Date().toISOString()
      });

      // Procesar datos reales de transacciones primero
      const realTransactionProcessed = processRealTransactionData(transactionData);

      if (realTransactionProcessed) {
        // Combinar datos reales de transacciones con cotizaciones
        // console.log('📊 Combinando datos reales de transacciones con cotizaciones');

        // Agregar cotizaciones a los KPIs (sumando count correcto)
        realTransactionProcessed.kpis.totalCotizaciones = quotationData.reduce((sum, q) => sum + (q.count || 0), 0);

        // Procesar datos de cotizaciones para las gráficas
        const quotationsByDay = quotationData.reduce((acc, q) => {
          const date = q.fecha;
          if (!acc[date]) acc[date] = 0;
          acc[date] += (q.count || 0);
          return acc;
        }, {});

        // Actualizar dailyTrends con unión de fechas de transacciones y cotizaciones
        const existingByDate = (realTransactionProcessed.dailyTrends || []).reduce((acc, day) => {
          acc[day.date] = day;
          return acc;
        }, {});
        const dateSet = new Set([
          ...Object.keys(existingByDate),
          ...Object.keys(quotationsByDay)
        ]);
        const allDatesUnion = Array.from(dateSet).sort();
        realTransactionProcessed.dailyTrends = allDatesUnion.map(date => ({
          date,
          cotizaciones: quotationsByDay[date] || 0,
          retomas: existingByDate[date]?.retomas || 0,
          canceladas: existingByDate[date]?.canceladas || 0,
          montoRefurbi: existingByDate[date]?.montoRefurbi || 0,
          montoMarcas: existingByDate[date]?.montoMarcas || 0
        }));

        // Procesar datos apilados de cotizaciones por retailer-marca
        const quotationsByRetailerMarca = quotationData.reduce((acc, q) => {
          const key = `${q.retailer}-${q.marca}`;
          if (!acc[key]) acc[key] = 0;
          acc[key] += (q.count || 0);
          return acc;
        }, {});

        // Actualizar stackedData con cotizaciones
        Object.keys(realTransactionProcessed.stackedData).forEach(key => {
          realTransactionProcessed.stackedData[key].cotizaciones = quotationsByRetailerMarca[key] || 0;
        });

        // Agregar nuevas combinaciones de retailer-marca que solo tienen cotizaciones
        Object.keys(quotationsByRetailerMarca).forEach(key => {
          if (!realTransactionProcessed.stackedData[key]) {
            const [retailer, marca] = key.split('-');
            realTransactionProcessed.stackedData[key] = {
              retailer,
              marca,
              retomas: 0,
              cotizaciones: quotationsByRetailerMarca[key],
              montoRefurbi: 0,
              montoMarcas: 0
            };
          }
        });

        // Actualizar dailyComboTransacciones con cotizaciones por fecha y combo
        const quotationsByDateCombo = quotationData.reduce((acc, q) => {
          const date = q.fecha;
          const key = `${q.retailer}-${q.marca}`;
          if (!acc[date]) acc[date] = {};
          if (!acc[date][key]) acc[date][key] = { retomas: 0, cotizaciones: 0 };
          acc[date][key].cotizaciones += (q.count || 0);
          return acc;
        }, {});

        // Combinar cotizaciones con datos de transacciones existentes
        Object.keys(quotationsByDateCombo).forEach(date => {
          if (!realTransactionProcessed.dailyComboTransacciones[date]) {
            realTransactionProcessed.dailyComboTransacciones[date] = {};
          }
          Object.keys(quotationsByDateCombo[date]).forEach(key => {
            if (!realTransactionProcessed.dailyComboTransacciones[date][key]) {
              realTransactionProcessed.dailyComboTransacciones[date][key] = { retomas: 0, canceladas: 0, cotizaciones: 0 };
            } else {
              // Asegurar propiedades
              realTransactionProcessed.dailyComboTransacciones[date][key].retomas = realTransactionProcessed.dailyComboTransacciones[date][key].retomas || 0;
              realTransactionProcessed.dailyComboTransacciones[date][key].canceladas = realTransactionProcessed.dailyComboTransacciones[date][key].canceladas || 0;
              realTransactionProcessed.dailyComboTransacciones[date][key].cotizaciones = realTransactionProcessed.dailyComboTransacciones[date][key].cotizaciones || 0;
            }
            realTransactionProcessed.dailyComboTransacciones[date][key].cotizaciones += quotationsByDateCombo[date][key].cotizaciones;
          });
        });

        // Edge case: si NO hay ninguna transacción finalizada, asegurar que existan combos solo con cotizaciones
        const hasAnyRetoma = Object.values(realTransactionProcessed.dailyComboTransacciones).some(dayCombos =>
          Object.values(dayCombos).some(combo => (combo.retomas || 0) > 0)
        );
        if (!hasAnyRetoma) {
          // Asegurar dailyTrends y dailyComboTransacciones consistentes solo con cotizaciones
          realTransactionProcessed.dailyTrends = realTransactionProcessed.dailyTrends.map(day => ({
            ...day,
            retomas: day.retomas || 0,
            montoRefurbi: 0,
            montoMarcas: 0
          }));
        }

        setDashboardData(realTransactionProcessed);
        // Enforzar reglas de visibilidad por rol con datos reales
        await enforceRoleRestrictions(transactionData, quotationData, realTransactionProcessed);
      } else {
        // Procesar datos con filtros aplicados (método anterior)
        // console.log('📊 Usando método anterior de procesamiento');
        const processedData = applyFiltersToData(quotationData, transactionData, filters);
        setDashboardData(processedData);
        await enforceRoleRestrictions(transactionData, quotationData, processedData);
      }

      // console.log('✅ Datos procesados y actualizados');

      // Auto-colapsar solo si se solicita explícitamente
      if (autoCollapse) {
        setFiltersCollapsed(true);
      }

    } catch (error) {
      // console.error('❌ Error al cargar datos reales:', error);
      setError('Error al cargar datos del servidor. Mostrando datos de ejemplo.');
      // Mantener datos mock en caso de error
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos reales al montar el componente
  useEffect(() => {
    loadRealData();
  }, []);

  // Función para aplicar filtros
  const handleApplyFilters = () => {
    // Si las fechas cambiaron, recargar datos de la API
    const currentData = realData;
    if (!currentData.quotations || !currentData.transactions) {
      loadRealData();
      return;
    }

    setLoading(true);
    try {
      // Aplicar filtros a los datos existentes
      const processedData = applyFiltersToData(currentData.quotations, currentData.transactions, filters);
      setDashboardData(processedData);
      // console.log('✅ Filtros aplicados');
    } catch (error) {
      // console.error('❌ Error al aplicar filtros:', error);
    } finally {
      setLoading(false);
    }

    // Auto-colapsar filtros después de aplicar
    setFiltersCollapsed(true);
  };

  // Función para aplicar solo filtros de fecha (recarga datos)
  const handleApplyDateFilters = () => {
    loadRealData(true); // Pasar true para auto-colapsar
  };

  // Función para aplicar filtros a datos reales ya procesados
  const applyFiltersToProcessedData = (rawTransactionData, rawQuotationData, currentFilters) => {
    // console.log('🔍 Aplicando filtros:', currentFilters);
    // console.log('📊 Datos originales - Transacciones:', rawTransactionData?.length || 0);
    // console.log('📊 Datos originales - Cotizaciones:', rawQuotationData?.length || 0);

    // Validar que tenemos datos
    if (!rawTransactionData || !Array.isArray(rawTransactionData)) {
      // console.log('❌ No hay datos de transacciones válidos para filtrar');
      return null;
    }

    if (!rawQuotationData || !Array.isArray(rawQuotationData)) {
      // console.log('❌ No hay datos de cotizaciones válidos para filtrar');
      return null;
    }

    // Filtrar transacciones reales
    const filteredTransactions = rawTransactionData.filter(transaction => {
      if (currentFilters.retailer && transaction.retailer !== currentFilters.retailer) return false;
      if (currentFilters.brand && transaction.brand !== currentFilters.brand) return false;
      if (currentFilters.store && transaction.store_name !== currentFilters.store) return false;
      return true;
    });

    // console.log('✅ Transacciones filtradas:', filteredTransactions.length);

    // Filtrar cotizaciones (respetando count)
    const filteredQuotations = rawQuotationData.filter(quotation => {
      if (currentFilters.retailer && quotation.retailer !== currentFilters.retailer) return false;
      if (currentFilters.brand && quotation.marca !== currentFilters.brand) return false;
      if (currentFilters.store && quotation.tienda !== currentFilters.store) return false;
      return true;
    });

    // console.log('✅ Cotizaciones filtradas:', filteredQuotations.length);

    // Procesar datos filtrados usando la misma lógica que los datos completos
    const filteredProcessedData = processRealTransactionData(filteredTransactions);

    if (filteredProcessedData) {
      // Agregar cotizaciones filtradas (sumando count)
      filteredProcessedData.kpis.totalCotizaciones = filteredQuotations.reduce((sum, q) => sum + (q.count || 0), 0);

      // Procesar cotizaciones filtradas por día
      const quotationsByDay = filteredQuotations.reduce((acc, q) => {
        const date = q.fecha;
        if (!acc[date]) acc[date] = 0;
        acc[date] += (q.count || 0);
        return acc;
      }, {});

      // Actualizar dailyTrends con unión de fechas de transacciones y cotizaciones filtradas
      const existingByDate = (filteredProcessedData.dailyTrends || []).reduce((acc, day) => {
        acc[day.date] = day;
        return acc;
      }, {});
      const dateSet = new Set([
        ...Object.keys(existingByDate),
        ...Object.keys(quotationsByDay)
      ]);
      const allDatesUnion = Array.from(dateSet).sort();
      filteredProcessedData.dailyTrends = allDatesUnion.map(date => ({
        date,
        cotizaciones: quotationsByDay[date] || 0,
        retomas: existingByDate[date]?.retomas || 0,
        canceladas: existingByDate[date]?.canceladas || 0,
        montoRefurbi: existingByDate[date]?.montoRefurbi || 0,
        montoMarcas: existingByDate[date]?.montoMarcas || 0
      }));

      // Procesar datos apilados de cotizaciones filtradas por retailer-marca
      const quotationsByRetailerMarca = filteredQuotations.reduce((acc, q) => {
        const key = `${q.retailer}-${q.marca}`;
        if (!acc[key]) acc[key] = 0;
        acc[key] += (q.count || 0);
        return acc;
      }, {});

      // Actualizar stackedData con cotizaciones filtradas
      Object.keys(filteredProcessedData.stackedData).forEach(key => {
        filteredProcessedData.stackedData[key].cotizaciones = quotationsByRetailerMarca[key] || 0;
      });

      // Agregar nuevas combinaciones de retailer-marca que solo tienen cotizaciones filtradas
      Object.keys(quotationsByRetailerMarca).forEach(key => {
        if (!filteredProcessedData.stackedData[key]) {
          const [retailer, marca] = key.split('-');
          filteredProcessedData.stackedData[key] = {
            retailer,
            marca,
            retomas: 0,
            cotizaciones: quotationsByRetailerMarca[key],
            montoRefurbi: 0,
            montoMarcas: 0
          };
        }
      });

      // Actualizar dailyComboTransacciones con cotizaciones filtradas por fecha y combo
      const quotationsByDateCombo = filteredQuotations.reduce((acc, q) => {
        const date = q.fecha;
        const key = `${q.retailer}-${q.marca}`;
        if (!acc[date]) acc[date] = {};
        if (!acc[date][key]) acc[date][key] = { retomas: 0, canceladas: 0, cotizaciones: 0 };
        acc[date][key].cotizaciones += (q.count || 0);
        return acc;
      }, {});

      // Combinar cotizaciones filtradas con datos de transacciones existentes
      Object.keys(quotationsByDateCombo).forEach(date => {
        if (!filteredProcessedData.dailyComboTransacciones[date]) {
          filteredProcessedData.dailyComboTransacciones[date] = {};
        }
        Object.keys(quotationsByDateCombo[date]).forEach(key => {
          if (!filteredProcessedData.dailyComboTransacciones[date][key]) {
            filteredProcessedData.dailyComboTransacciones[date][key] = { retomas: 0, canceladas: 0, cotizaciones: 0 };
          } else {
            filteredProcessedData.dailyComboTransacciones[date][key].retomas = filteredProcessedData.dailyComboTransacciones[date][key].retomas || 0;
            filteredProcessedData.dailyComboTransacciones[date][key].canceladas = filteredProcessedData.dailyComboTransacciones[date][key].canceladas || 0;
            filteredProcessedData.dailyComboTransacciones[date][key].cotizaciones = filteredProcessedData.dailyComboTransacciones[date][key].cotizaciones || 0;
          }
          filteredProcessedData.dailyComboTransacciones[date][key].cotizaciones += quotationsByDateCombo[date][key].cotizaciones;
        });
      });

      // console.log('🎉 Datos filtrados procesados exitosamente');
      return filteredProcessedData;
    }

    // console.log('❌ No se pudieron procesar los datos filtrados');
    return null;
  };

  // Función para aplicar filtros de visualización (sin recargar datos)
  const handleApplyViewFilters = async () => {
    const currentData = realData;
    if (!currentData.quotations || !currentData.transactions) {
      return;
    }

    setLoading(true);
    try {
      // Enforzar que el filtro restringido no quede vacío
      const role = userRole || await getUserRole();
      let enforcedFilters = { ...filters };
      if (role === 'admin_retailer' && !enforcedFilters.brand) {
        const fallbackBrand = getFirstBrandFromData(dashboardData, currentData.quotations, currentData.transactions);
        if (fallbackBrand) enforcedFilters.brand = fallbackBrand;
      }
      if (role === 'admin_marca' && !enforcedFilters.retailer) {
        const fallbackRetailer = getFirstRetailerFromData(dashboardData, currentData.quotations, currentData.transactions);
        if (fallbackRetailer) enforcedFilters.retailer = fallbackRetailer;
      }
      setFilters(enforcedFilters);
      // Obtener datos raw de transacciones directamente del estado fuente (evitar desincronización)
      const rawTransactions = currentData.transactions || [];

      // console.log('🔧 Usando datos raw de transacciones:', rawTransactions.length);

      // Usar la nueva función que mantiene la estructura completa
      const filteredData = applyFiltersToProcessedData(
        rawTransactions,
        currentData.quotations,
        enforcedFilters
      );

      if (filteredData) {
        setDashboardData({ ...filteredData, rawTransactionData: currentData.transactions || [] });
        // console.log('✅ Filtros de visualización aplicados con datos reales');
      } else {
        // Fallback al método anterior si no hay datos reales
        const processedData = applyFiltersToData(currentData.quotations, currentData.transactions, enforcedFilters);
        setDashboardData({ ...processedData, rawTransactionData: currentData.transactions || [] });
        // console.log('✅ Filtros aplicados con método fallback');
      }
    } catch (error) {
      // console.error('❌ Error al aplicar filtros de visualización:', error);
    } finally {
      setLoading(false);
    }

    // Auto-colapsar panel después de aplicar filtros
    setFiltersCollapsed(true);
  };

  // Función para limpiar filtros
  const handleClearFilters = () => {
    const dateRange = getDefaultDateRange();
    setFilters({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      retailer: '',
      brand: '',
      store: ''
    });
    // Mantener filtros expandidos al limpiar
    setFiltersCollapsed(false);
  };

  // Función para limpiar solo filtros de visualización
  const handleClearViewFilters = async () => {
    // Resetear filtros de visualización
    let clearedFilters = {
      ...filters,
      retailer: '',
      brand: '',
      store: ''
    };
    // Enforzar restricción por rol para no dejar vacío el filtro protegido
    const role = userRole || await getUserRole();
    if (role === 'admin_retailer') {
      const fallbackBrand = getFirstBrandFromData(dashboardData, realData.quotations, realData.transactions);
      if (fallbackBrand) clearedFilters.brand = fallbackBrand;
    }
    if (role === 'admin_marca') {
      const fallbackRetailer = getFirstRetailerFromData(dashboardData, realData.quotations, realData.transactions);
      if (fallbackRetailer) clearedFilters.retailer = fallbackRetailer;
    }
    setFilters(clearedFilters);

    // Reaplicar datos filtrados según el estado base del rol
    const currentData = realData;
    if (!currentData.quotations || !currentData.transactions) {
      loadRealData();
      return;
    }

    setLoading(true);
    try {
      const rawTransactions = currentData.transactions || [];
      const filteredData = applyFiltersToProcessedData(
        rawTransactions,
        currentData.quotations,
        clearedFilters
      );
      if (filteredData) {
        setDashboardData({ ...filteredData, rawTransactionData: currentData.transactions || [] });
      } else {
        const processedData = applyFiltersToData(currentData.quotations, currentData.transactions, clearedFilters);
        setDashboardData({ ...processedData, rawTransactionData: currentData.transactions || [] });
      }
    } catch (_) {
    } finally {
      setLoading(false);
    }

    // Auto-colapsar panel después de limpiar filtros
    setFiltersCollapsed(true);
  };

  // Función para toggle de filtros (clickeando en el título cuando está expandido)
  const handleToggleFilters = () => {
    setFiltersCollapsed(!filtersCollapsed);
  };

  // Función para toggle de filtros (mantener compatibilidad con el botón)
  const toggleFilters = () => {
    setFiltersCollapsed(!filtersCollapsed);
  };

  // Función para expandir filtros (clickeando en cualquier parte del área colapsada)
  const handleExpandFilters = () => {
    if (filtersCollapsed) {
      setFiltersCollapsed(false);
    }
  };

  // Función para exportar datos a Excel
  const handleExportExcel = async () => {
    try {
      console.log('📊 Iniciando exportación a Excel con datos completos...');

      // Mostrar loading mientras se obtienen los datos completos
      setLoading(true);

      // Obtener datos completos desde S3 (descarga CSV, parsea y convierte)
      const completeTransactionsData = await fetchTransactionDataForExport(
        filters.startDate,
        filters.endDate
      );

      console.log(`📊 Datos completos obtenidos: ${completeTransactionsData.length} transacciones`);

      // Obtener datos de cotizaciones (estos siguen siendo ligeros)
      const quotationsData = realData.quotations || [];
      const quotationsForExcel = quotationsData.map(q => ({
        // Cargar como Date seguro (local) desde yyyy-mm-dd
        'Fecha': (() => { const [yy, mm, dd] = (q.fecha || '').split('-').map(Number); return new Date(yy, (mm || 1) - 1, dd || 1); })(),
        'Retailer': q.retailer,
        'Marca': q.marca,
        'Tienda': q.tienda,
        'Cantidad': q.count
      }));

      // Mapear los datos completos del CSV a formato Excel
      // Los nombres de columnas del CSV deben coincidir con los que envía Lambda
      const transactionsForExcel = completeTransactionsData.map(t => ({
        'ID Transacción': t.transaction_id || t['transaction_id'],
        'Fecha': t.timestamp ? new Date(Number(t.timestamp) * 1000) : null,
        'Dispositivo': t['buyback_device_name'],
        'IMEI 1': t['buyback_device_imei'],
        'IMEI 2': t['buyback_device_imei_2'],
        'Dispositivo Almacenamiento': t['buyback_device_storage'],
        'Adicionales': t['additional_items'],
        'Retailer': t.retailer,
        'Marca': t.brand,
        'Tercero - Nombre': t['customer_customer_name'],
        'Tercero - Apellido': t['customer_customer_surname'],
        'Tercero - Cédula': t['customer_customer_id'],
        'Tercero - Correo': t['customer_customer_email'],
        'Tercero - Dirección': t['customer_customer_address'],
        'Tercero - Ciudad': t['customer_customer_city'],
        'Tercero - Departametno': t['customer_customer_department'],
        'Tercero - Teléfono': t['customer_customer_phone'],
        'Tienda': t.store_name || t['store_name'],
        'Ciudad': t.store_city || t['store_city'],
        'Promotor': t.promoter_name || t['promoter_name'],
        'ID Promotor': t.promoter_id || t['promoter_id'],
        'Dispositivo Retomado': t.buyback_device_name_std || t['buyback_device_name_std'],
        'Marca Dispositivo': t.buyback_device_brand || t['buyback_device_brand'],
        'Grado Final': t.buyback_device_final_grade || t['buyback_device_final_grade'],
        'Dispositivo Vendido': t.sold_device_name || t['sold_device_name'],
        'Precio Venta': Math.round(parseFloat(t.sold_device_price || t['sold_device_price']) || 0),
        'Monto Refurbi': Math.round(parseFloat(t.bonus_amount_trader || t['bonus_amount_trader']) || 0),
        'Monto Refurbi Adicionales': Math.round(parseFloat(t.bonus_amount_additional || t['bonus_amount_additional']) || 0),
        'Monto Marca': Math.round(parseFloat(t.bonus_amount_campaign || t['bonus_amount_campaign']) || 0),
        'Tipo Transacción': t.tradein_type || t['tradein_type'],
        'Estado': (t.tradein_status || t['tradein_status'] || '').toLowerCase() === 'cancelada' ? 'cancelada' : 'finalizada',
        'CANCELADO POR - ID': t.cancelled_by_id || t['cancelled_by_id'] || '',
        'CANCELADO POR - NOMBRE': t.cancelled_by_name || t['cancelled_by_name'] || '',
        'CANCELADO POR - ROL': t.cancelled_by_rol || t['cancelled_by_rol'] || '',
        'CANCELADO POR - FECHA': (t.cancelled_date || t['cancelled_date']) ? new Date((Number(t.cancelled_date || t['cancelled_date']) || 0) * 1000) : null,
        'CANCELADO POR - RAZÓN': t.cancelled_reason || t['cancelled_reason'] || ''
      }));

      // Crear workbook
      const wb = XLSX.utils.book_new();

      // Crear hoja de cotizaciones
      const wsQuotations = XLSX.utils.json_to_sheet(quotationsForExcel);
      XLSX.utils.book_append_sheet(wb, wsQuotations, 'Cotizaciones');

      // Crear hoja de transacciones
      const wsTransactions = XLSX.utils.json_to_sheet(transactionsForExcel);
      XLSX.utils.book_append_sheet(wb, wsTransactions, 'Transacciones Finalizadas');

      // Aplicar formato de fecha a columnas de Fecha usando serial Excel (robusto)
      const dateToExcelSerial = (d) => d instanceof Date && !isNaN(d) ? (d.getTime() / 86400000) + 25569 : null;

      const getHeaderColumnIndex = (ws, headerLabel) => {
        try {
          const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
          for (let c = range.s.c; c <= range.e.c; c++) {
            const addr = XLSX.utils.encode_cell({ r: range.s.r, c });
            const cell = ws[addr];
            if (cell && String(cell.v).trim() === headerLabel) return c;
          }
        } catch (_) { }
        return -1;
      };

      try {
        const rangeQ = XLSX.utils.decode_range(wsQuotations['!ref'] || 'A1');
        const fechaColQ = getHeaderColumnIndex(wsQuotations, 'Fecha');
        if (fechaColQ >= 0) {
          for (let r = rangeQ.s.r + 1; r <= rangeQ.e.r; r++) {
            const qIdx = r - (rangeQ.s.r + 1);
            const q = quotationsData[qIdx];
            if (!q) continue;
            const [yy, mm, dd] = (q.fecha || '').split('-').map(Number);
            const d = new Date(yy, (mm || 1) - 1, dd || 1);
            const serial = dateToExcelSerial(d);
            if (serial) {
              const cellAddr = XLSX.utils.encode_cell({ r, c: fechaColQ });
              const cell = wsQuotations[cellAddr] || {};
              cell.t = 'n';
              cell.v = serial;
              cell.z = 'yyyy-mm-dd';
              wsQuotations[cellAddr] = cell;
            }
          }
        }
      } catch (_) { }

      try {
        const rangeT = XLSX.utils.decode_range(wsTransactions['!ref'] || 'A1');
        const fechaColT = getHeaderColumnIndex(wsTransactions, 'Fecha');
        const cancelFechaColT = getHeaderColumnIndex(wsTransactions, 'CANCELADO POR - FECHA');
        for (let r = rangeT.s.r + 1; r <= rangeT.e.r; r++) {
          const tIdx = r - (rangeT.s.r + 1);
          const t = completeTransactionsData[tIdx];
          if (!t) continue;
          if (fechaColT >= 0) {
            const timestamp = t.timestamp || t['timestamp'];
            const d = new Date((Number(timestamp) || 0) * 1000);
            const serial = dateToExcelSerial(d);
            if (serial) {
              const cellAddr = XLSX.utils.encode_cell({ r, c: fechaColT });
              const cell = wsTransactions[cellAddr] || {};
              cell.t = 'n';
              cell.v = serial;
              cell.z = 'd/m/yyyy';
              wsTransactions[cellAddr] = cell;
            }
          }
          if (cancelFechaColT >= 0 && (t.cancelled_date || t['cancelled_date'])) {
            const cancelDate = t.cancelled_date || t['cancelled_date'];
            const d2 = new Date((Number(cancelDate) || 0) * 1000);
            const serial2 = dateToExcelSerial(d2);
            if (serial2) {
              const cellAddr2 = XLSX.utils.encode_cell({ r, c: cancelFechaColT });
              const cell2 = wsTransactions[cellAddr2] || {};
              cell2.t = 'n';
              cell2.v = serial2;
              cell2.z = 'd/m/yyyy';
              wsTransactions[cellAddr2] = cell2;
            }
          }
        }
      } catch (_) { }

      // Generar archivo Excel
      const fileName = `Dashboard_Ejecutivo_${filters.startDate}_${filters.endDate}.xlsx`;
      XLSX.writeFile(wb, fileName);

      console.log('✅ Archivo Excel generado exitosamente');

    } catch (error) {
      console.error('❌ Error al exportar Excel:', error);
      alert('Error al generar el archivo Excel. Por favor, intenta de nuevo.\n\nDetalle: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  // Función para exportar datos a PDF
  const handleExportPDF = () => {
    try {
      // Crear ventana de impresión para PDF
      const printWindow = window.open('', '_blank');

      // CSS específico para PDF
      const pdfStyles = `
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #6366F1; padding-bottom: 20px; }
          .logo { font-size: 24px; font-weight: bold; color: #6366F1; }
          .title { font-size: 20px; margin: 10px 0; }
          .date-range { font-size: 14px; color: #666; }
          .kpi-section { margin: 30px 0; }
          .kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 30px; }
          .kpi-card { border: 1px solid #ddd; padding: 15px; border-radius: 8px; text-align: center; }
          .kpi-number { font-size: 24px; font-weight: bold; color: #6366F1; }
          .kpi-label { font-size: 12px; color: #666; margin-top: 5px; }
          .rankings-section { margin-top: 30px; }
          .rankings-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
          .ranking-card { border: 1px solid #ddd; padding: 15px; border-radius: 8px; }
          .ranking-header { position: relative; padding-right: 2rem; margin-bottom: 15px; display: flex; align-items: center; }
          .ranking-title  { flex:1; text-align:center; margin:0; }
          .toggle-personal-data-btn {
            position: absolute;
            top: 0.25rem;
            right: 0.25rem;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: none;
            border-radius: 999px;
            background: transparent;
            color: inherit;
            cursor: pointer;
            transition: transform .15s ease, background-color .15s ease, opacity .15s ease;
            opacity: .85;
          }
          .toggle-personal-data-btn:hover {
            opacity: 1;
            transform: scale(1.06);
            background-color: rgba(255,255,255,.12);
          }
          .ranking-item { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; padding: 8px; background: #f9f9f9; border-radius: 4px; }
          .ranking-position { font-weight: bold; color: #6366F1; width: 30px; }
          .ranking-content { flex: 1; margin-left: 10px; }
          .ranking-name { font-weight: bold; }
          .ranking-subtitle { font-size: 12px; color: #666; }
          .ranking-value { font-weight: bold; }
          .page-break { page-break-before: always; }
          @media print { body { margin: 0; } }
        </style>
      `;

      // Generar contenido HTML para PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Dashboard Ejecutivo - Retoma</title>
          ${pdfStyles}
        </head>
        <body>
          <div class="header">
            <div class="logo">♻️ Retoma</div>
            <div class="title">Dashboard Ejecutivo</div>
            <div class="date-range">Período: ${filters.startDate} al ${filters.endDate}</div>
            <div class="date-range">Generado: ${new Date().toLocaleString('es-CO')}</div>
          </div>

          <div class="kpi-section">
            <h2>Indicadores Clave de Rendimiento</h2>
            <div class="kpi-grid">
              <div class="kpi-card">
                <div class="kpi-number">${formatNumber(dashboardData.kpis.totalCotizaciones || 0)}</div>
                <div class="kpi-label">Cotizaciones Realizadas</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-number">${formatNumber(dashboardData.kpis.totalRetomas)}</div>
                <div class="kpi-label">Retomas Finalizadas</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-number">${formatCurrency(dashboardData.kpis.totalVentasGeneradas || 0)}</div>
                <div class="kpi-label">Ventas Totales Generadas</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-number">${formatCurrency(dashboardData.kpis.totalMontoRefurbi || 0)}</div>
                <div class="kpi-label">Monto Total Refurbi</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-number">${formatCurrency(dashboardData.kpis.totalMontoMarcas || 0)}</div>
                <div class="kpi-label">Monto Total Marcas</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-number">${formatNumber(dashboardData.kpis.tiendasActivas)}</div>
                <div class="kpi-label">Tiendas Activas</div>
              </div>
            </div>
          </div>

          <div class="rankings-section">
            <h2>Rankings Top 5</h2>
            <div class="rankings-grid">
              <div class="ranking-card">
                <div class="ranking-title">🏆 Top 5 Tiendas - Retomas</div>
                ${(dashboardData.topStores || []).map((store, index) => `
                  <div class="ranking-item">
                    <div class="ranking-position">${index + 1}</div>
                    <div class="ranking-content">
                      <div class="ranking-name">${store.name}</div>
                      <div class="ranking-subtitle">${store.retailer}</div>
                    </div>
                    <div class="ranking-value">${formatNumber(store.retomas)}</div>
                  </div>
                `).join('')}
              </div>

              <div class="ranking-card">
                <div class="ranking-header">
                  <div class="ranking-title">👨‍💼 Top 5 Promotores - Retomas</div>
                  <button
                    type="button"
                    class="toggle-personal-data-btn"
                    aria-pressed="${showPersonalData}"
                    onclick="(() => { setShowPersonalData(!showPersonalData); updateDashboard(); })()"
                    title="${showPersonalData ? 'Ocultar IDs' : 'Mostrar IDs'}"
                  >
                    ${showPersonalData ? '🔒' : '👁️'}
                  </button>
                </div>
                ${(dashboardData.topPromoters || []).map((promoter, index) => `
                  <div class="ranking-item">
                    <div class="ranking-position">${index + 1}</div>
                    <div class="ranking-content">
                      <div class="ranking-name">${promoter.name}</div>
                      ${showPersonalData ? `<div class="ranking-subtitle">ID: ${promoter.id}</div>` : ''}
                    </div>
                    <div class="ranking-value">${formatNumber(promoter.quantity)}</div>
                  </div>
                `).join('')}
              </div>

              <div class="ranking-card">
                <div class="ranking-title">📱 Top 5 Celulares - Retomas</div>
                ${(dashboardData.topDevicesRetomas || []).map((device, index) => `
                  <div class="ranking-item">
                    <div class="ranking-position">${index + 1}</div>
                    <div class="ranking-content">
                      <div class="ranking-name">${device.model}</div>
                      <div class="ranking-subtitle">${device.brand}</div>
                    </div>
                    <div class="ranking-value">${formatNumber(device.quantity)}</div>
                  </div>
                `).join('')}
              </div>

              <div class="ranking-card">
                <div class="ranking-title">🛍️ Top 5 Celulares - Ventas</div>
                ${(dashboardData.topDevicesVendidos || []).map((device, index) => `
                  <div class="ranking-item">
                    <div class="ranking-position">${index + 1}</div>
                    <div class="ranking-content">
                      <div class="ranking-name">${device.model}</div>
                      <div class="ranking-subtitle">${device.brand} • ${formatNumber(device.quantity)} unidades</div>
                    </div>
                    <div class="ranking-value">${formatCompactNumber(device.totalValue)}</div>
                  </div>
                `).join('')}
              </div>

              <div class="ranking-card">
                <div class="ranking-title">🏙️ Top 5 Ciudades - Retomas</div>
                ${(dashboardData.topCities || []).map((city, index) => `
                  <div class="ranking-item">
                    <div class="ranking-position">${index + 1}</div>
                    <div class="ranking-content">
                      <div class="ranking-name">${city.name}</div>
                      <div class="ranking-subtitle">Retomas realizadas</div>
                    </div>
                    <div class="ranking-value">${formatNumber(city.quantity)}</div>
                  </div>
                `).join('')}
              </div>

              <div class="ranking-card">
                <div class="ranking-title">🏷️ Top 5 Marcas - Retomas</div>
                ${(dashboardData.topBrandRetomas || []).map((brand, index) => `
                  <div class="ranking-item">
                    <div class="ranking-position">${index + 1}</div>
                    <div class="ranking-content">
                      <div class="ranking-name">${brand.brand}</div>
                      <div class="ranking-subtitle">Dispositivos retomados</div>
                    </div>
                    <div class="ranking-value">${formatNumber(brand.quantity)}</div>
                  </div>
                `).join('')}
              </div>
            </div>
          </div>
        </body>
        </html>
      `;

      // Escribir contenido y abrir diálogo de impresión
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Esperar a que se cargue y abrir diálogo de impresión
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      };

    } catch (error) {
      console.error('Error al generar PDF:', error);
      alert('Error al generar el PDF. Por favor, intenta de nuevo.');
    }
  };

  // Función para exportar datos
  const handleExportData = (format) => {
    if (format === 'Excel') {
      handleExportExcel();
    } else if (format === 'PDF') {
      handleExportPDF();
    }
  };

  // Función para formatear números
  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-CO').format(num);
  };

  // Función para formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Función para formatear fecha
  const formatDate = (dateStr) => {
    if (!dateStr) return '';

    // Crear fecha local para evitar problemas de zona horaria
    // Parsear la fecha como YYYY-MM-DD y crear una fecha local
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month es 0-indexed

    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short'
    });
  };

  // Función para formatear números compactos (máximo 3 cifras + letra)
  const formatCompactNumber = (num) => {
    if (num === 0) return '0';

    const absNum = Math.abs(num);
    const sign = num < 0 ? '-' : '';

    if (absNum >= 1000000000) {
      return sign + (absNum / 1000000000).toFixed(absNum >= 10000000000 ? 0 : absNum >= 1000000000 ? 1 : 2).replace(/\.?0+$/, '') + 'B';
    } else if (absNum >= 1000000) {
      return sign + (absNum / 1000000).toFixed(absNum >= 10000000 ? 0 : absNum >= 1000000 ? 1 : 2).replace(/\.?0+$/, '') + 'M';
    } else if (absNum >= 1000) {
      return sign + (absNum / 1000).toFixed(absNum >= 10000 ? 0 : absNum >= 1000 ? 1 : 2).replace(/\.?0+$/, '') + 'k';
    } else {
      return sign + absNum.toString();
    }
  };

  // Función para generar datos apilados para retomas y cotizaciones - USANDO DATOS REALES
  const getStackedTransactionData = () => {
    const colors = ['#6366F1', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#84CC16'];

    if (!dashboardData.dailyComboTransacciones || !dashboardData.dailyTrends) return [];

    return dashboardData.dailyTrends.map(day => {
      const date = day.date;
      const combosForDay = dashboardData.dailyComboTransacciones[date] || {};

      if (chartMode === 'transacciones') {
        // Segmentar por finalizadas vs canceladas
        const retomasSegments = Object.entries(combosForDay).map(([combo, data], index) => ({
          combo,
          value: data.retomas || 0,
          color: colors[index % colors.length],
          type: 'retomas'
        })).filter(segment => segment.value > 0);

        const canceladasSegments = Object.entries(combosForDay).map(([combo, data], index) => ({
          combo,
          value: data.canceladas || 0,
          color: colors[(index + 3) % colors.length],
          type: 'canceladas'
        })).filter(segment => segment.value > 0);

        return {
          date,
          retomasSegments,
          canceladasSegments,
          totalRetomas: Math.max(day.retomas || 0, 0),
          totalCanceladas: Math.max(day.canceladas || 0, 0)
        };
      }

      // Modo cotizaciones
      const cotizacionesSegments = Object.entries(combosForDay).map(([combo, data], index) => ({
        combo,
        value: data.cotizaciones || 0,
        color: colors[index % colors.length],
        type: 'cotizaciones'
      })).filter(segment => segment.value > 0);

      return {
        date,
        cotizacionesSegments,
        totalCotizaciones: Math.max(day.cotizaciones || 0, 0)
      };
    });
  };

  // Función para generar datos de gráfico de torta de grados
  const getGradeDistributionData = () => {
    if (!dashboardData.gradeDistribution) return [];

    const gradeLabels = {
      'A': 'Excelente (A)',
      'B': 'Bueno (B)',
      'C': 'Aceptable (C)'
    };

    const gradeColors = {
      'A': '#10B981',
      'B': '#F59E0B',
      'C': '#EF4444'
    };

    const total = Object.values(dashboardData.gradeDistribution).reduce((sum, count) => sum + count, 0);

    return Object.entries(dashboardData.gradeDistribution).map(([grade, count]) => ({
      grade,
      label: gradeLabels[grade] || grade,
      count,
      color: gradeColors[grade] || '#6B7280',
      percentage: total > 0 ? ((count / total) * 100).toFixed(1) : '0.0'
    }));
  };

  // Función para generar datos de gráfico de tipos de transacción
  const getTransactionTypeData = () => {
    if (!dashboardData.typeDistribution) return [];

    const typeLabels = {
      'física': 'Transacciones Físicas',
      'manual': 'Transacciones Manuales',
      'virtual': 'Transacciones Virtuales'
    };

    const typeColors = {
      'física': '#10B981',
      'manual': '#F59E0B',
      'virtual': '#6366F1'
    };

    const total = Object.values(dashboardData.typeDistribution).reduce((sum, count) => sum + count, 0);

    return Object.entries(dashboardData.typeDistribution).map(([type, count]) => ({
      type,
      label: typeLabels[type] || type,
      count,
      color: typeColors[type] || '#6B7280',
      percentage: total > 0 ? ((count / total) * 100).toFixed(1) : '0.0'
    }));
  };

  // Función para generar datos apilados para montos por fuente - USANDO DATOS REALES
  const getStackedMoneyData = () => {
    const colors = ['#6366F1', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#84CC16'];

    if (!dashboardData.dailyComboMoney || !dashboardData.dailyTrends) return [];

    return dashboardData.dailyTrends.map(day => {
      const date = day.date;
      const combosForDay = dashboardData.dailyComboMoney[date] || {};

      // Crear segmentos para Refurbi usando datos reales del día
      const refurbiSegments = Object.entries(combosForDay).map(([combo, data], index) => ({
        combo,
        value: data.montoRefurbi || 0,
        color: colors[index % colors.length],
        type: 'refurbi'
      })).filter(segment => segment.value > 0);

      // Crear segmentos para Marcas usando datos reales del día
      const marcasSegments = Object.entries(combosForDay).map(([combo, data], index) => ({
        combo,
        value: data.montoMarcas || 0,
        color: colors[index % colors.length],
        type: 'marcas'
      })).filter(segment => segment.value > 0);

      return {
        date,
        refurbiSegments,
        marcasSegments,
        totalRefurbi: Math.max(day.montoRefurbi || 0, 0),
        totalMarcas: Math.max(day.montoMarcas || 0, 0)
      };
    });
  };

  // Validar acceso por rol
  useEffect(() => {
    const validateAccess = async () => {
      try {
        // console.log('🔒 [DASHBOARD] Validando acceso del usuario...');

        const userHasAccess = await hasAccess(ALLOWED_ROLES);

        if (!userHasAccess) {
          // console.log('❌ [DASHBOARD] Usuario sin permisos de acceso');
          // Si no tiene permiso, limpiar sesión y redirigir
          localStorage.clear();
          navigate('/');
        } else {
          // console.log('✅ [DASHBOARD] Acceso del usuario validado');
          // Capturar y guardar rol para lógica de UI/filtros
          try {
            const role = await getUserRole();
            setUserRole(role);
          } catch (_) { }
        }
      } catch (error) {
        // console.error('💥 [DASHBOARD] Error al validar acceso:', error);
        // En caso de error, también redirigir por seguridad
        localStorage.clear();
        navigate('/');
      }
    };

    validateAccess();
  }, [navigate]);

  return (
    <div className="dashboard-ejecutivo-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
      <header className="header header-sticky">
        <Logo
          variant="horizontal"
          size="medium"
          onClick={() => navigate('/dashboard')}
        />
        <div className="header-title">
          Dashboard Ejecutivo
          {realData.lastUpdated && (
            <div className="data-status">
              📊 Datos actualizados: {new Date(realData.lastUpdated).toLocaleString('es-CO')}
            </div>
          )}
        </div>
        <div className="user-menu">
          <span className="user-name">{userData.user_name || preferredName || 'Usuario'}</span>
          <button className="btn btn-outline" onClick={() => navigate('/dashboard')}>
            Volver al Menú
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        {/* Mensaje de error si existe */}
        {error && (
          <div className="error-message-banner">
            ⚠️ {error}
          </div>
        )}

        {/* Tarjeta Unificada de Controles - Colapsable */}
        <div className={`unified-controls-section card unified-controls-sticky ${filtersCollapsed ? 'collapsed' : 'expanded'}`}>
          <div
            className="unified-controls-header"
            onClick={filtersCollapsed ? handleExpandFilters : handleToggleFilters}
            style={{ cursor: 'pointer' }}
          >
            <div className="unified-controls-header-content">
              <h3 className="unified-controls-title">⚙️ Panel de Control</h3>
              <div className="unified-controls-subtitle">
                {filtersCollapsed ?
                  'Haz clic para acceder a filtros, consultas y exportación' :
                  'Haz clic para colapsar el panel de control'
                }
              </div>
            </div>
            <button
              className="unified-controls-toggle-btn"
              onClick={(e) => {
                e.stopPropagation();
                toggleFilters();
              }}
              title={filtersCollapsed ? 'Expandir panel de control' : 'Colapsar panel de control'}
            >
              {filtersCollapsed ? '⚙️' : '⬆️'}
            </button>
          </div>

          <div className={`unified-controls-content ${filtersCollapsed ? 'hidden' : 'visible'}`}>
            <div className="controls-grid">
              {/* Sección 1: Período de Consulta */}
              <div className="control-section date-section">
                <div className="section-header">
                  <h4 className="section-title">
                    📅 Período de Consulta
                    <button
                      className="info-tooltip-btn"
                      title="Selecciona el rango de fechas para obtener datos de la base de datos"
                    >
                      ❓
                    </button>
                  </h4>
                </div>
                <div className="section-content">
                  <div className="date-controls-vertical">
                    <div className="filter-group">
                      <label className="form-label">Fecha Inicio</label>
                      <input
                        type="date"
                        className="form-input"
                        value={filters.startDate}
                        onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                      />
                    </div>

                    <div className="filter-group">
                      <label className="form-label">Fecha Fin</label>
                      <input
                        type="date"
                        className="form-input"
                        value={filters.endDate}
                        onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                      />
                    </div>

                    <div className="date-actions">
                      <button
                        className="btn btn-primary btn-full-width"
                        onClick={handleApplyDateFilters}
                        disabled={loading}
                      >
                        {loading ? '⏳ Cargando...' : '🔄 Obtener Datos'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sección 2: Filtros de Visualización */}
              <div className="control-section filters-section">
                <div className="section-header">
                  <h4 className="section-title">
                    🔍 Filtros de Visualización
                    <button
                      className="info-tooltip-btn"
                      title="Filtra los datos sin realizar nuevas consultas a la base de datos"
                    >
                      ❓
                    </button>
                  </h4>
                </div>
                <div className="section-content">
                  <div className="filters-controls-vertical">
                    <div className="filter-group">
                      <label className="form-label">Retailer</label>
                      <select
                        className="form-select"
                        value={filters.retailer}
                        onChange={(e) => {
                          const value = e.target.value;
                          // En admin_marca no permitir valor vacío (todos)
                          if (userRole === 'admin_marca' && value === '') return;
                          setFilters({ ...filters, retailer: value });
                        }}
                      >
                        {userRole !== 'admin_marca' && (
                          <option value="">Todos los retailers</option>
                        )}
                        {(allRetailers.length > 0 ? allRetailers : dashboardData.retailers).map(retailer => (
                          <option key={retailer} value={retailer}>{retailer}</option>
                        ))}
                      </select>
                    </div>

                    <div className="filter-group">
                      <label className="form-label">Marca</label>
                      <select
                        className="form-select"
                        value={filters.brand}
                        onChange={(e) => {
                          const value = e.target.value;
                          // En admin_retailer no permitir valor vacío (todas)
                          if (userRole === 'admin_retailer' && value === '') return;
                          setFilters({ ...filters, brand: value });
                        }}
                      >
                        {userRole !== 'admin_retailer' && (
                          <option value="">Todas las marcas</option>
                        )}
                        {(allBrands.length > 0 ? allBrands : dashboardData.brands).map(brand => (
                          <option key={brand} value={brand}>{brand}</option>
                        ))}
                      </select>
                    </div>

                    <div className="filter-group">
                      <label className="form-label">Tienda</label>
                      <select
                        className="form-select"
                        value={filters.store}
                        onChange={(e) => setFilters({ ...filters, store: e.target.value })}
                      >
                        <option value="">Todas las tiendas</option>
                        {dashboardData.stores.map(store => (
                          <option key={store} value={store}>{store}</option>
                        ))}
                      </select>
                    </div>

                    <div className="filters-actions">
                      <button
                        className="btn btn-primary btn-full-width"
                        onClick={handleApplyViewFilters}
                        disabled={loading}
                      >
                        {loading ? '⏳ Aplicando...' : '🔎 Aplicar Filtros'}
                      </button>
                      <button
                        className="btn btn-outline btn-full-width"
                        onClick={handleClearViewFilters}
                      >
                        🗑️ Limpiar Filtros
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sección 3: Exportación */}
              <div className="control-section export-section">
                <div className="section-header">
                  <h4 className="section-title">
                    📊 Exportación de Datos
                    <button
                      className="info-tooltip-btn"
                      title="Descarga los datos filtrados en diferentes formatos"
                    >
                      ❓
                    </button>
                  </h4>
                </div>
                <div className="section-content">
                  <div className="export-controls-vertical">
                    <button
                      className="btn btn-secondary btn-export btn-full-width"
                      onClick={() => handleExportData('Excel')}
                    >
                      📊 Exportar Excel
                    </button>
                    <button
                      className="btn btn-outline btn-export btn-full-width"
                      onClick={() => handleExportData('PDF')}
                    >
                      📑 Exportar PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="kpi-section">
          <div className="kpi-grid kpi-grid-7">
            {/* Fila superior: 4 tarjetas compactas */}
            <div className="kpi-card kpi-compact">
              <div className="kpi-icon">📋</div>
              <div className="kpi-content">
                <div className="kpi-number">{formatNumber(dashboardData.kpis.totalCotizaciones || 0)}</div>
                <div className="kpi-label">Cotizaciones Realizadas</div>
              </div>
            </div>

            <div className="kpi-card kpi-compact">
              <div className="kpi-icon">✅</div>
              <div className="kpi-content">
                <div className="kpi-number">{formatNumber(dashboardData.kpis.totalRetomas)}</div>
                <div className="kpi-label">Retomas Finalizadas</div>
              </div>
            </div>

            <div className="kpi-card kpi-compact">
              <div className="kpi-icon">🏪</div>
              <div className="kpi-content">
                <div className="kpi-number">{formatNumber(dashboardData.kpis.tiendasActivas)}</div>
                <div className="kpi-label">Tiendas Activas</div>
              </div>
            </div>

            <div className="kpi-card kpi-compact kpi-canceladas">
              <div className="kpi-icon">🚫</div>
              <div className="kpi-content">
                <div className="kpi-number">{formatNumber(dashboardData.kpis.totalCanceladas || 0)}</div>
                <div className="kpi-label">Transacciones Canceladas</div>
              </div>
            </div>

            {/* Fila inferior: 3 tarjetas de monto/impacto */}
            <div className="kpi-card kpi-card-highlight">
              <div className="kpi-icon">🛍️</div>
              <div className="kpi-content">
                <div className="kpi-number">{formatCurrency(dashboardData.kpis.totalVentasGeneradas || 0)}</div>
                <div className="kpi-label">Ventas Totales Generadas</div>
                <div className="kpi-sublabel">Impacto del programa</div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon">💰</div>
              <div className="kpi-content">
                <div className="kpi-number">{formatCurrency(dashboardData.kpis.totalMontoRefurbi || dashboardData.kpis.montoTotalRefurbi || 0)}</div>
                <div className="kpi-label">{getDynamicTenantTexts().totalAmountLabel}</div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon">🏢</div>
              <div className="kpi-content">
                <div className="kpi-number">{formatCurrency(dashboardData.kpis.totalMontoMarcas || dashboardData.kpis.montoTotalMarcas || 0)}</div>
                <div className="kpi-label">Monto Total Entregado por Marca</div>
              </div>
            </div>
          </div>
        </div>

        {/* Gráficas Principales */}
        <div className="charts-section">
          <div className="charts-grid">
            {/* Gráfica principal (selector Transacciones/Cotizaciones) */}
            <div className="chart-card">
              <div className="chart-header">
                <div className="chart-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <h3 className="chart-title">
                      {chartMode === 'transacciones' ? '📈 Finalizadas vs Canceladas por Retailer-Marca' : '📈 Cotizaciones por Retailer-Marca'}
                    </h3>
                    <p className="chart-subtitle">
                      {chartMode === 'transacciones' ? 'Barras dobles: Finalizadas (sólido) vs Canceladas (patrón diagonal)' : 'Barras simples: Cotizaciones por día, segmentadas por retailer-marca'}
                    </p>
                  </div>
                  <div>
                    <label className="form-label" style={{ marginRight: 8 }}>Modo</label>
                    <select className="form-select" value={chartMode} onChange={(e) => setChartMode(e.target.value)}>
                      <option value="transacciones">Transacciones</option>
                      <option value="cotizaciones">Cotizaciones</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="chart-container">
                {/* Eje Y con etiquetas y líneas perfectamente alineadas */}
                <div className="chart-y-axis">
                  {(() => {
                    const dataForAxis = getStackedTransactionData();
                    const maxValueRaw = chartMode === 'transacciones'
                      ? Math.max(...dataForAxis.map(d => Math.max(d.totalRetomas || 0, d.totalCanceladas || 0)))
                      : Math.max(...dataForAxis.map(d => d.totalCotizaciones || 0));
                    const maxValue = Number.isFinite(maxValueRaw) && maxValueRaw > 0 ? maxValueRaw : 1;
                    // Calcular intervalos dinámicos basados en el valor máximo
                    const steps = 5;
                    const interval = Math.ceil(maxValue / steps);
                    const dynamicMax = interval * steps;

                    return Array.from({ length: steps + 1 }, (_, i) => {
                      const value = i * interval;
                      return (
                        <div key={i}>
                          <div className="y-axis-line"></div>
                          <span className="y-axis-label">{formatCompactNumber(value)}</span>
                        </div>
                      );
                    }).reverse(); // Invertir para que el valor más alto esté arriba
                  })()}
                </div>
                <div className="chart-bars">
                  {getStackedTransactionData().map((dayData, index) => (
                    <div key={index} className="bar-group">
                      <div className="bar-container">
                        {chartMode === 'transacciones' ? (
                          <>
                            {/* Barra Finalizadas (sólido) */}
                            <div
                              className="stacked-daily-bar dual-bar"
                              style={{
                                height: `${(() => {
                                  const allData = getStackedTransactionData();
                                  const maxValueRaw = Math.max(...allData.map(d => Math.max(d.totalRetomas || 0, d.totalCanceladas || 0)));
                                  const maxValue = Number.isFinite(maxValueRaw) && maxValueRaw > 0 ? maxValueRaw : 1;
                                  const steps = 5;
                                  const interval = Math.ceil(maxValue / steps);
                                  const dynamicMax = interval * steps;
                                  return Math.max(((dayData.totalRetomas || 0) / dynamicMax) * 100, 0);
                                })()}%`
                              }}
                            >
                              {(dayData.retomasSegments || []).map((segment) => {
                                const percentage = (dayData.totalRetomas ? (segment.value / dayData.totalRetomas) * 100 : 0);
                                return (
                                  <div
                                    key={`retomas-${segment.combo}`}
                                    className="bar-segment solid-pattern"
                                    style={{ height: `${percentage}%`, backgroundColor: segment.color }}
                                    title={`Finalizadas ${segment.combo}: ${formatNumber(segment.value)}`}
                                  />
                                );
                              })}
                            </div>

                            {/* Barra Canceladas (patrón diagonal) */}
                            <div
                              className="stacked-daily-bar dual-bar"
                              style={{
                                height: `${(() => {
                                  const allData = getStackedTransactionData();
                                  const maxValueRaw = Math.max(...allData.map(d => Math.max(d.totalRetomas || 0, d.totalCanceladas || 0)));
                                  const maxValue = Number.isFinite(maxValueRaw) && maxValueRaw > 0 ? maxValueRaw : 1;
                                  const steps = 5;
                                  const interval = Math.ceil(maxValue / steps);
                                  const dynamicMax = interval * steps;
                                  return Math.max(((dayData.totalCanceladas || 0) / dynamicMax) * 100, 0);
                                })()}%`
                              }}
                            >
                              {(dayData.canceladasSegments || []).map((segment) => {
                                const percentage = (dayData.totalCanceladas ? (segment.value / dayData.totalCanceladas) * 100 : 0);
                                return (
                                  <div
                                    key={`canceladas-${segment.combo}`}
                                    className="bar-segment diagonal-pattern"
                                    style={{ height: `${percentage}%`, backgroundColor: segment.color }}
                                    title={`Canceladas ${segment.combo}: ${formatNumber(segment.value)}`}
                                  />
                                );
                              })}
                            </div>
                          </>
                        ) : (
                          // Modo cotizaciones: una sola barra
                          <div
                            className="stacked-daily-bar dual-bar"
                            style={{
                              height: `${(() => {
                                const allData = getStackedTransactionData();
                                const maxValueRaw = Math.max(...allData.map(d => d.totalCotizaciones || 0));
                                const maxValue = Number.isFinite(maxValueRaw) && maxValueRaw > 0 ? maxValueRaw : 1;
                                const steps = 5;
                                const interval = Math.ceil(maxValue / steps);
                                const dynamicMax = interval * steps;
                                return Math.max(((dayData.totalCotizaciones || 0) / dynamicMax) * 100, 0);
                              })()}%`
                            }}
                          >
                            {(dayData.cotizacionesSegments || []).map((segment) => {
                              const percentage = (dayData.totalCotizaciones ? (segment.value / dayData.totalCotizaciones) * 100 : 0);
                              return (
                                <div
                                  key={`cotizaciones-${segment.combo}`}
                                  className="bar-segment solid-pattern"
                                  style={{ height: `${percentage}%`, backgroundColor: segment.color }}
                                  title={`Cotizaciones ${segment.combo}: ${formatNumber(segment.value)}`}
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="bar-label">{formatDate(dayData.date)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Gráfica Apilada de Montos por Retailer-Marca y Fuente */}
            <div className="chart-card">
              <div className="chart-header">
                <h3 className="chart-title">💸 Montos Apilados por Retailer-Marca</h3>
                <p className="chart-subtitle">{getDynamicTenantTexts().doubleBarsLabel}</p>
              </div>
              <div className="chart-container">
                {/* Eje Y con etiquetas de montos y líneas perfectamente alineadas */}
                <div className="chart-y-axis">
                  {(() => {
                    const moneyAxisData = getStackedMoneyData();
                    const maxMoneyRaw = Math.max(...moneyAxisData.map(d => Math.max(d.totalRefurbi, d.totalMarcas)));
                    const maxValue = Number.isFinite(maxMoneyRaw) && maxMoneyRaw > 0 ? maxMoneyRaw : 1;
                    // Calcular intervalos dinámicos basados en el valor máximo
                    const steps = 5;
                    const interval = Math.ceil(maxValue / steps);
                    const dynamicMax = interval * steps;

                    return Array.from({ length: steps + 1 }, (_, i) => {
                      const value = i * interval;
                      return (
                        <div key={i}>
                          <div className="y-axis-line"></div>
                          <span className="y-axis-label money-axis">{formatCompactNumber(value)}</span>
                        </div>
                      );
                    }).reverse(); // Invertir para que el valor más alto esté arriba
                  })()}
                </div>
                <div className="chart-bars">
                  {getStackedMoneyData().map((dayData, index) => (
                    <div key={index} className="bar-group">
                      <div className="bar-container">
                        {/* Barra de Refurbi */}
                        <div
                          className="stacked-daily-bar dual-bar"
                          style={{
                            height: `${(() => {
                              const allData = getStackedMoneyData();
                              const maxMoneyRaw = Math.max(...allData.map(d => Math.max(d.totalRefurbi, d.totalMarcas)));
                              const maxValue = Number.isFinite(maxMoneyRaw) && maxMoneyRaw > 0 ? maxMoneyRaw : 1;
                              const steps = 5;
                              const interval = Math.ceil(maxValue / steps);
                              const dynamicMax = interval * steps;
                              return Math.max((dayData.totalRefurbi / dynamicMax) * 100, 0);
                            })()}%`
                          }}
                          title={`Refurbi Total: ${formatCurrency(dayData.totalRefurbi)}`}
                        >
                          {dayData.refurbiSegments.map((segment, segIndex) => {
                            const percentage = (segment.value / dayData.totalRefurbi) * 100;
                            return (
                              <div
                                key={`refurbi-${segment.combo}`}
                                className="bar-segment solid-pattern"
                                style={{
                                  height: `${percentage}%`,
                                  backgroundColor: segment.color,
                                  opacity: 0.8
                                }}
                                title={`Refurbi ${segment.combo}: ${formatCurrency(segment.value)}`}
                              />
                            );
                          })}
                        </div>

                        {/* Barra de Marcas */}
                        <div
                          className="stacked-daily-bar dual-bar"
                          style={{
                            height: `${(() => {
                              const allData = getStackedMoneyData();
                              const maxMoneyRaw = Math.max(...allData.map(d => Math.max(d.totalRefurbi, d.totalMarcas)));
                              const maxValue = Number.isFinite(maxMoneyRaw) && maxMoneyRaw > 0 ? maxMoneyRaw : 1;
                              const steps = 5;
                              const interval = Math.ceil(maxValue / steps);
                              const dynamicMax = interval * steps;
                              return Math.max((dayData.totalMarcas / dynamicMax) * 100, 0);
                            })()}%`
                          }}
                          title={`Marcas Total: ${formatCurrency(dayData.totalMarcas)}`}
                        >
                          {dayData.marcasSegments.map((segment, segIndex) => {
                            const percentage = (segment.value / dayData.totalMarcas) * 100;
                            return (
                              <div
                                key={`marcas-${segment.combo}`}
                                className="bar-segment diagonal-pattern"
                                style={{
                                  height: `${percentage}%`,
                                  backgroundColor: segment.color,
                                  opacity: 0.6
                                }}
                                title={`Marcas ${segment.combo}: ${formatCurrency(segment.value)}`}
                              />
                            );
                          })}
                        </div>
                      </div>
                      <div className="bar-label">{formatDate(dayData.date)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gráficas de Análisis Adicionales */}
        <div className="analysis-charts-section">
          <div className="analysis-charts-grid">
            {/* Gráfico de Torta - Distribución de Grados */}
            <div className="chart-card">
              <div className="chart-header">
                <h3 className="chart-title">📊 Distribución de Grados de Dispositivos</h3>
                <p className="chart-subtitle">Clasificación por estado de los dispositivos retomados</p>
              </div>
              <div className="pie-chart-container">
                <div className="pie-chart-visual">
                  {/* SVG para la torta interactiva */}
                  <svg width="200" height="200" className="interactive-pie-chart">
                    {(() => {
                      const data = getGradeDistributionData();
                      const total = data.reduce((sum, item) => sum + item.count, 0);
                      let cumulativePercentage = 0;

                      return data.map((item, index) => {
                        const percentage = (item.count / total) * 100;
                        const startAngle = (cumulativePercentage / 100) * 360;
                        const endAngle = ((cumulativePercentage + percentage) / 100) * 360;

                        // Calcular coordenadas del segmento SVG
                        const radius = 90;
                        const centerX = 100;
                        const centerY = 100;

                        const startX = centerX + radius * Math.cos((startAngle - 90) * Math.PI / 180);
                        const startY = centerY + radius * Math.sin((startAngle - 90) * Math.PI / 180);
                        const endX = centerX + radius * Math.cos((endAngle - 90) * Math.PI / 180);
                        const endY = centerY + radius * Math.sin((endAngle - 90) * Math.PI / 180);

                        const largeArcFlag = percentage > 50 ? 1 : 0;

                        const pathData = [
                          `M ${centerX},${centerY}`,
                          `L ${startX},${startY}`,
                          `A ${radius},${radius} 0 ${largeArcFlag},1 ${endX},${endY}`,
                          'Z'
                        ].join(' ');

                        cumulativePercentage += percentage;

                        return (
                          <path
                            key={item.grade}
                            d={pathData}
                            fill={item.color}
                            stroke="white"
                            strokeWidth="2"
                            className="pie-segment"
                            onMouseEnter={() => {
                              const info = document.getElementById('grade-chart-info');
                              if (info) info.textContent = `${item.label}: ${item.count} (${item.percentage}%)`;
                            }}
                            onMouseLeave={() => {
                              const info = document.getElementById('grade-chart-info');
                              if (info) info.textContent = 'Información de grado';
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                        );
                      });
                    })()}
                  </svg>
                  <div className="pie-center-info" id="grade-chart-info">
                    Información de grado
                  </div>
                </div>
                <div className="pie-chart-legend">
                  {getGradeDistributionData().map((item) => (
                    <div
                      key={item.grade}
                      className="legend-item"
                    >
                      <div className="legend-color" style={{ backgroundColor: item.color }}></div>
                      <span className="legend-label">{item.label}</span>
                      <span className="legend-value">{item.count} ({item.percentage}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Gráfico de Torta - Tipos de Transacción */}
            <div className="chart-card">
              <div className="chart-header">
                <h3 className="chart-title">🔄 Distribución de Tipos de Transacción</h3>
                <p className="chart-subtitle">Análisis de flujo físico vs manual (indicador de calidad)</p>
              </div>
              <div className="pie-chart-container">
                <div className="pie-chart-visual">
                  {/* SVG para la torta interactiva */}
                  <svg width="200" height="200" className="interactive-pie-chart">
                    {(() => {
                      const data = getTransactionTypeData();
                      const total = data.reduce((sum, item) => sum + item.count, 0);
                      let cumulativePercentage = 0;

                      return data.map((item, index) => {
                        const percentage = (item.count / total) * 100;
                        const startAngle = (cumulativePercentage / 100) * 360;
                        const endAngle = ((cumulativePercentage + percentage) / 100) * 360;

                        // Calcular coordenadas del segmento SVG
                        const radius = 90;
                        const centerX = 100;
                        const centerY = 100;

                        const startX = centerX + radius * Math.cos((startAngle - 90) * Math.PI / 180);
                        const startY = centerY + radius * Math.sin((startAngle - 90) * Math.PI / 180);
                        const endX = centerX + radius * Math.cos((endAngle - 90) * Math.PI / 180);
                        const endY = centerY + radius * Math.sin((endAngle - 90) * Math.PI / 180);

                        const largeArcFlag = percentage > 50 ? 1 : 0;

                        const pathData = [
                          `M ${centerX},${centerY}`,
                          `L ${startX},${startY}`,
                          `A ${radius},${radius} 0 ${largeArcFlag},1 ${endX},${endY}`,
                          'Z'
                        ].join(' ');

                        cumulativePercentage += percentage;

                        return (
                          <path
                            key={item.type}
                            d={pathData}
                            fill={item.color}
                            stroke="white"
                            strokeWidth="2"
                            className="pie-segment"
                            onMouseEnter={() => {
                              const info = document.getElementById('type-chart-info');
                              if (info) info.textContent = `${item.label}: ${item.count} (${item.percentage}%)`;
                            }}
                            onMouseLeave={() => {
                              const info = document.getElementById('type-chart-info');
                              if (info) info.textContent = 'Información de tipo';
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                        );
                      });
                    })()}
                  </svg>
                  <div className="pie-center-info" id="type-chart-info">
                    Información de tipo
                  </div>
                </div>
                <div className="pie-chart-legend">
                  {getTransactionTypeData().map((item) => (
                    <div
                      key={item.type}
                      className="legend-item"
                    >
                      <div className="legend-color" style={{ backgroundColor: item.color }}></div>
                      <span className="legend-label">{item.label}</span>
                      <span className="legend-value">{item.count} ({item.percentage}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Rankings Top 5 */}
        <div className="rankings-section">
          <div className="rankings-grid">
            {/* Top 5 Tiendas */}
            <div className="ranking-card">
              <div className="ranking-header">
                <h3 className="ranking-title">🏆 Top 5 Tiendas - Retomas</h3>
              </div>
              <div className="ranking-list">
                {(dashboardData.topStores || []).map((store, index) => (
                  <div key={index} className="ranking-item">
                    <div className="ranking-position">{index + 1}</div>
                    <div className="ranking-content">
                      <div className="ranking-name">{store.name}</div>
                      <div className="ranking-subtitle">{store.retailer}</div>
                    </div>
                    <div className="ranking-value">{formatNumber(store.retomas)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top 5 Promotores */}
            <div className="ranking-card">
              <div className="ranking-header">
                <h3 className="ranking-title">👨‍💼 Top 5 Promotores - Retomas</h3>
                <button
                  type="button"
                  className="toggle-personal-data-btn"
                  aria-pressed={showPersonalData}
                  onClick={() => setShowPersonalData(!showPersonalData)}
                  title={showPersonalData ? "Ocultar IDs" : "Mostrar IDs"}
                >
                  {showPersonalData ? "🔒" : "👁️"}
                </button>
              </div>
              <div className="ranking-list">
                {(dashboardData.topPromoters || []).map((promoter, index) => (
                  <div key={index} className="ranking-item">
                    <div className="ranking-position">{index + 1}</div>
                    <div className="ranking-content">
                      <div className="ranking-name">{promoter.name}</div>
                      {showPersonalData && (
                        <div className="ranking-subtitle">ID: {promoter.id}</div>
                      )}
                    </div>
                    <div className="ranking-value">{formatNumber(promoter.quantity)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top 5 Celulares Retomados */}
            <div className="ranking-card">
              <div className="ranking-header">
                <h3 className="ranking-title">📱 Top 5 Celulares - Retomas</h3>
              </div>
              <div className="ranking-list">
                {(dashboardData.topDevicesRetomas || []).map((device, index) => (
                  <div key={index} className="ranking-item">
                    <div className="ranking-position">{index + 1}</div>
                    <div className="ranking-content">
                      <div className="ranking-name">{device.model}</div>
                      <div className="ranking-subtitle">{device.brand}</div>
                    </div>
                    <div className="ranking-value">{formatNumber(device.quantity)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top 5 Celulares Vendidos */}
            <div className="ranking-card">
              <div className="ranking-header">
                <h3 className="ranking-title">🛍️ Top 5 Celulares - Ventas</h3>
              </div>
              <div className="ranking-list">
                {(dashboardData.topDevicesVendidos || []).map((device, index) => (
                  <div key={index} className="ranking-item">
                    <div className="ranking-position">{index + 1}</div>
                    <div className="ranking-content">
                      <div className="ranking-name">{device.model}</div>
                      <div className="ranking-subtitle">{device.brand} • {formatNumber(device.quantity)} unidades</div>
                    </div>
                    <div className="ranking-value">{formatCompactNumber(device.totalValue)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top 5 Ciudades */}
            <div className="ranking-card">
              <div className="ranking-header">
                <h3 className="ranking-title">🏙️ Top 5 Ciudades - Retomas</h3>
              </div>
              <div className="ranking-list">
                {(dashboardData.topCities || []).map((city, index) => (
                  <div key={index} className="ranking-item">
                    <div className="ranking-position">{index + 1}</div>
                    <div className="ranking-content">
                      <div className="ranking-name">{city.name}</div>
                      <div className="ranking-subtitle">Retomas realizadas</div>
                    </div>
                    <div className="ranking-value">{formatNumber(city.quantity)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top 5 Marcas Retomadas */}
            <div className="ranking-card">
              <div className="ranking-header">
                <h3 className="ranking-title">🏷️ Top 5 Marcas - Retomas</h3>
              </div>
              <div className="ranking-list">
                {(dashboardData.topBrandRetomas || []).map((brand, index) => (
                  <div key={index} className="ranking-item">
                    <div className="ranking-position">{index + 1}</div>
                    <div className="ranking-content">
                      <div className="ranking-name">{brand.brand}</div>
                      <div className="ranking-subtitle">Dispositivos retomados</div>
                    </div>
                    <div className="ranking-value">{formatNumber(brand.quantity)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardEjecutivo;

// Estilos CSS adicionales para el dashboard ejecutivo
const dashboardStyles = `
  .data-status {
    font-size: 0.75rem;
    color: white;
    margin-top: 0.25rem;
    font-weight: normal;
    opacity: 0.9;
  }
  
  .header-title {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }
  
  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #0066cc;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  .error-message-banner {
    background-color: #fff3cd;
    border: 1px solid #ffeaa7;
    color: #856404;
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 6px;
    text-align: center;
    font-weight: 500;
  }
  
  /* Estilos para la tarjeta unificada de controles */
  .unified-controls-section {
    background: white;
    border: 1px solid #e5e7eb;
    margin-bottom: 1.5rem;
    transition: all 0.3s ease;
    border-radius: 8px;
    overflow: hidden;
  }
  
  .unified-controls-sticky {
    position: sticky;
    top: 80px;
    z-index: 100;
  }
  
  .unified-controls-section.collapsed {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  .unified-controls-section.expanded {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }
  
  .unified-controls-header {
    padding: 1.25rem 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e5e7eb;
    transition: all 0.3s ease;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }
  
  .unified-controls-section.collapsed .unified-controls-header {
    border-bottom: none;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    color: #374151;
  }
  
  .unified-controls-section.collapsed .unified-controls-header:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%);
  }
  
  .unified-controls-header-content {
    flex: 1;
  }
  
  .unified-controls-title {
    font-size: 1.2rem;
    font-weight: 600;
    margin: 0;
  }
  
  .unified-controls-subtitle {
    font-size: 0.9rem;
    opacity: 0.8;
    margin-top: 0.25rem;
  }
  
  .unified-controls-toggle-btn {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;
    font-size: 1.2rem;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 6px;
    transition: all 0.2s ease;
  }
  
  .unified-controls-section.collapsed .unified-controls-toggle-btn {
    background: rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(0, 0, 0, 0.1);
    color: #374151;
  }
  
  .unified-controls-toggle-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
  
  .unified-controls-section.collapsed .unified-controls-toggle-btn:hover {
    background: rgba(0, 0, 0, 0.1);
  }
  
  .unified-controls-content {
    transition: all 0.4s ease;
    overflow: hidden;
  }
  
  .unified-controls-content.visible {
    max-height: 800px;
    opacity: 1;
    padding: 1.5rem;
  }
  
  .unified-controls-content.hidden {
    max-height: 0;
    opacity: 0;
    padding: 0 1.5rem;
  }
  
  /* Grid horizontal para las tres secciones */
  .controls-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 1.5rem;
    align-items: start;
  }
  
  /* Estilos para las secciones internas */
  .control-section {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  .section-header {
    margin-bottom: 1rem;
    flex-shrink: 0;
  }
  
  .section-title {
    font-size: 1rem;
    font-weight: 600;
    margin: 0;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .info-tooltip-btn {
    background: rgba(0, 0, 0, 0.1);
    border: none;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    font-size: 0.7rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    color: #6b7280;
  }
  
  .info-tooltip-btn:hover {
    background: rgba(0, 0, 0, 0.2);
    transform: scale(1.1);
  }
  
  .section-content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  
  /* Sección de fechas */
  .date-section {
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    padding: 1.25rem;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
  }
  
  .date-controls-vertical {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    height: 100%;
  }
  
  .date-actions {
    margin-top: auto;
  }
  
  /* Sección de filtros */
  .filters-section {
    background: #fefefe;
    padding: 1.25rem;
    border-radius: 8px;
    border: 1px solid #f3f4f6;
  }
  
  .filters-controls-vertical {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    height: 100%;
  }
  
  .filters-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: auto;
  }
  
  /* Sección de exportación */
  .export-section {
    background: linear-gradient(135deg, #fef7cd 0%, #fef3c7 100%);
    padding: 1.25rem;
    border-radius: 8px;
    border: 1px solid #fde68a;
  }
  
  .export-controls-vertical {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    height: 100%;
  }
  
  .btn-export {
    padding: 0.6rem 1.2rem;
    font-weight: 500;
    transition: all 0.2s ease;
  }
  
  .btn-export:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  .btn-full-width {
    width: 100%;
    justify-content: center;
  }
  
  /* Responsividad */
  @media (max-width: 1024px) {
    .controls-grid {
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
    }
    
    .export-section {
      grid-column: 1 / -1;
    }
    
    .export-controls-vertical {
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
    }
    
    .export-section .btn-full-width {
      flex: 1;
      min-width: 150px;
    }
  }
  
  @media (max-width: 768px) {
    .controls-grid {
      grid-template-columns: 1fr;
      gap: 1rem;
    }
    
    .unified-controls-sticky {
      position: relative;
      top: auto;
    }
    
    .unified-controls-content.visible {
      padding: 1rem;
    }
    
    .export-controls-vertical {
      flex-direction: column;
    }
    
    .export-section .btn-full-width {
      width: 100%;
    }
  }
  
  @media (max-width: 480px) {
    .unified-controls-header {
      padding: 1rem;
    }
    
    .unified-controls-title {
      font-size: 1rem;
    }
    
    .unified-controls-subtitle {
      font-size: 0.8rem;
    }
    
    .section-title {
      font-size: 0.9rem;
    }
    
    .control-section {
      padding: 1rem;
    }
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* Estilos para contenedores de gráficas - LAYOUT CORREGIDO */
  .chart-container {
    position: relative;
    height: 350px; /* Altura fija para que los porcentajes funcionen */
    margin-top: 1rem;
    display: flex;
    overflow: hidden;
  }

  /* Contenedor del eje Y con líneas horizontales alineadas */
  .chart-y-axis {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
    padding-right: 8px;
    height: 100%;
    pointer-events: none;
    flex-shrink: 0;
    width: max-content;
    min-width: 40px;
  }

  .chart-y-axis > div {
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  /* Override específico para las etiquetas del eje Y en las gráficas */
  .chart-container .y-axis-label {
    position: relative !important;
    font-size: 0.7rem !important;
    color: #6b7280 !important;
    background: white !important;
    padding: 2px 4px !important;
    white-space: nowrap !important;
    line-height: 1.2 !important;
    text-align: right !important;
    z-index: 10 !important;
    display: block !important;
    border: 1px solid #e5e7eb !important;
    border-radius: 3px !important;
    font-weight: 500 !important;
    min-width: auto !important;
    left: auto !important;
    transform: none !important;
  }

  /* Líneas horizontales que se extienden desde el eje Y */
  .y-axis-line {
    position: absolute;
    top: 50%;
    left: 100%;
    width: 800px; /* Ancho generoso para que llegue al final */
    height: 1px;
    background-color: #e5e7eb;
    pointer-events: none;
    z-index: 1;
  }

  /* Contenedor de barras SIN padding problemático */
  .chart-bars {
    position: relative;
    display: flex;
    align-items: flex-end;
    justify-content: space-evenly;
    gap: 6px; /* Espaciado consistente entre barras */
    height: 100%;
    flex: 1;
    padding: 0;
    margin-left: 8px; /* Pequeño margen para separar de las líneas del eje Y */
  }

  /* Estilos para barras ancladas al fondo */
  .bar-container {
    display: flex;
    align-items: flex-end; /* Ancla las barras al fondo */
    height: 100%;
    justify-content: center;
    gap: 6px;
  }

  .stacked-daily-bar {
    width: 15px; /* Ancho fijo para las barras - aumentado para mejor visibilidad */
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  /* Posicionamiento específico para las barras duales */
  .stacked-daily-bar.dual-bar {
    width: 12px; /* Más delgadas para las barras duales - aumentado proporcionalmente */
  }

  .chart-container .y-axis-label.money-axis {
    font-weight: 600 !important;
    color: #059669 !important;
    font-size: 0.65rem !important;
    border-color: rgba(5, 150, 105, 0.3) !important;
    background: rgba(5, 150, 105, 0.05) !important;
  }

  /* Estilos para etiquetas de barras */
  .bar-label {
    position: absolute;
    bottom: -25px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.75rem;
    color: #6b7280;
    text-align: center;
    white-space: nowrap;
  }

  .bar-group {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
  }

  /* Estilos para segmentos de barras */
  .bar-segment {
    width: 100%;
    transition: opacity 0.2s ease;
  }

  .bar-segment.solid-pattern {
    background-image: none !important;
  }

  .bar-segment.diagonal-pattern {
    background-image: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 2px,
      rgba(255,255,255,0.3) 2px,
      rgba(255,255,255,0.3) 4px
    ) !important;
  }

  /* Actualizar grid KPI para 6 tarjetas con números largos */
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  }

  /* Estilos mejorados para números largos en KPIs */
  .kpi-number {
    font-size: 1.8rem !important;
    font-weight: 700 !important;
    line-height: 1.2 !important;
    word-break: break-word !important;
    overflow-wrap: break-word !important;
  }

  .kpi-card {
    min-height: 120px !important;
    padding: 1.25rem !important;
  }

  .kpi-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  @media (max-width: 1024px) {
    .kpi-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    
    .kpi-number {
      font-size: 1.6rem !important;
    }
  }

  @media (max-width: 768px) {
    .kpi-grid {
      grid-template-columns: 1fr;
    }
    
    .kpi-number {
      font-size: 1.4rem !important;
    }
    
    .kpi-card {
      min-height: 100px !important;
      padding: 1rem !important;
    }
  }

  /* Estilos para tarjetas KPI destacadas */
  .kpi-card-highlight {
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border: 2px solid #0ea5e9;
    position: relative;
    overflow: hidden;
  }

  /* Grid 7 tarjetas: 4 arriba compactas + 3 abajo más anchas */
  .kpi-grid-7 {
    grid-template-columns: repeat(4, 1fr);
    grid-auto-rows: 1fr;
  }
  .kpi-grid-7 .kpi-compact {
    min-height: 100px !important;
  }
  .kpi-grid-7 .kpi-card-highlight {
    grid-column: span 2;
  }
  .kpi-grid-7 .kpi-canceladas .kpi-icon {
    color: #ef4444;
  }
  .kpi-grid-7 .kpi-canceladas {
    border: 1px solid rgba(239,68,68,0.3);
    background: linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%);
  }

  @media (max-width: 1200px) {
    .kpi-grid-7 {
      grid-template-columns: repeat(3, 1fr);
    }
    .kpi-grid-7 .kpi-card-highlight {
      grid-column: span 3;
    }
  }
  @media (max-width: 900px) {
    .kpi-grid-7 {
      grid-template-columns: repeat(2, 1fr);
    }
    .kpi-grid-7 .kpi-card-highlight {
      grid-column: span 2;
    }
  }
  @media (max-width: 600px) {
    .kpi-grid-7 {
      grid-template-columns: 1fr;
    }
    .kpi-grid-7 .kpi-card-highlight {
      grid-column: span 1;
    }
  }

  .kpi-card-highlight::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(14, 165, 233, 0.1), transparent);
    animation: shimmer 3s infinite;
  }

  .kpi-card-highlight .kpi-icon {
    color: #0ea5e9;
    font-size: 2.5rem;
  }

  .kpi-sublabel {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.25rem;
    font-style: italic;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
    100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
  }

  /* Estilos para gráficas de análisis adicionales */
  .analysis-charts-section {
    margin-bottom: 1.5rem;
  }

  .analysis-charts-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }

  /* Estilos para gráficos de torta */
  .pie-chart-container {
    display: flex;
    align-items: center;
    gap: 2rem;
    padding: 1.5rem;
  }

  .pie-chart-visual {
    width: 200px;
    height: 200px;
    border-radius: 50%;
    position: relative;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .pie-chart-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }

  .pie-chart-legend {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
  }

  .legend-color {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .legend-label {
    flex: 1;
    font-weight: 500;
    color: #374151;
  }

  .legend-value {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 600;
  }

  /* Estilos para tortas SVG interactivas */
  .interactive-pie-chart {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }

  .pie-segment {
    transition: all 0.2s ease;
    filter: brightness(1);
  }

  .pie-segment:hover {
    filter: brightness(1.1);
    transform-origin: center;
    animation: pulseSegment 0.3s ease-in-out;
  }

  @keyframes pulseSegment {
    0% { transform: scale(1); }
    50% { transform: scale(1.02); }
    100% { transform: scale(1); }
  }

  /* Actualización del centro de información */
  .pie-center-info {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(255, 255, 255, 0.95);
    color: #374151;
    padding: 0.75rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 20;
    pointer-events: none;
    max-width: 120px;
    word-wrap: break-word;
    transition: all 0.2s ease;
  }

  /* Estilos para rankings destacados */
  .ranking-card-highlight {
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    border: 2px solid #f59e0b;
  }

  .ranking-card-highlight .ranking-title {
    color: #92400e;
  }

  .ranking-card-highlight .ranking-value {
    color: #b45309;
    font-weight: 700;
  }

  /* Actualizar grid de rankings para 6 tarjetas */
  .rankings-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: 1200px) {
    .rankings-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 768px) {
    .rankings-grid {
      grid-template-columns: 1fr;
    }
  }

  /* Responsividad para gráficas de análisis */
  @media (max-width: 1024px) {
    .analysis-charts-grid {
      grid-template-columns: 1fr;
      gap: 1rem;
    }
    
    .pie-chart-container {
      flex-direction: column;
      gap: 1rem;
    }
    
    .pie-chart-visual {
      width: 150px;
      height: 150px;
    }
  }

  @media (max-width: 768px) {
    .pie-chart-visual {
      width: 120px;
      height: 120px;
    }
    
    .pie-chart-container {
      padding: 1rem;
    }
    
    .legend-item {
      padding: 0.375rem;
    }
    
    /* Ajustes para pantallas pequeñas */
    .chart-container {
      gap: 4px;
    }
    
    .chart-bars {
      gap: 4px;
      margin-left: 4px;
    }
    
    .chart-y-axis {
      padding-right: 4px;
      min-width: 35px;
    }
    
    .chart-container .y-axis-label {
      font-size: 0.6rem !important;
      padding: 1px 2px !important;
    }
    
    .chart-container .y-axis-label.money-axis {
      font-size: 0.55rem !important;
    }
    
    .stacked-daily-bar {
      width: 12px;
    }
    
    .stacked-daily-bar.dual-bar {
      width: 10px;
    }
  }

  /* Estilos para botón toggle de datos personales */
  .ranking-header {
    position: relative;
    padding-right: 2rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
  }

  .ranking-title{
    flex: 1;           /* ocupa todo el ancho disponible */
    text-align: center;/* centra el texto */
    margin: 0;         /* quita márgenes del h3 */
  }

  .toggle-personal-data-btn {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    inline-size: 32px;
    block-size: 32px;
    display: grid;
    place-items: center;
    border: none;
    border-radius: 999px;
    background: transparent;
    color: inherit;
    appearance: none;
    cursor: pointer;
    transition: transform .15s ease, background-color .15s ease, opacity .15s ease;
    opacity: .85;
  }

  .toggle-personal-data-btn:hover {
    opacity: 1;
    transform: scale(1.06);
    background-color: rgba(255,255,255,.12);
  }

  .toggle-personal-data-btn:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

// Inyectar estilos si no existen
if (typeof document !== 'undefined' && !document.getElementById('dashboard-ejecutivo-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'dashboard-ejecutivo-styles';
  styleElement.textContent = dashboardStyles;
  document.head.appendChild(styleElement);
} 